import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { Device } from '@ionic-native/device/ngx';

import FingerprintJS from '@fingerprintjs/fingerprintjs';

import { Place } from './model/place';
import { User } from './model/user';
import { QrCode } from './model/qrcode';

import { AppConstants } from '@app/app.constants';
import { GenericUtils } from '@app/app.commonutils';

import { httpService } from '@app/modules/common/http';
import { storeService, SyncChange } from '@app/modules/store';
import { deviceService } from '@app/modules/sockets';
import { languageService } from '@app/modules/common/language';
import { alertService } from '@app/modules/common/alert';
import { toastService } from '@app/modules/common/toast';
import { platformService } from '@app/modules/common/platform';

/*
    TODO:
        - En ocasiones hay que esperar los 20 segundos del refresco para la primera carga de datos

    PENDING:
        - Incluir los horarios de apertura y cierre del local cada día de la semana
            - Esto se tiene que mostrar en la pantalla de bienvenida del guest
        - Hacer algo TBD para facilitar el scroll en pantallas tactiles

        - Añadir tipo de impresora para imprimir las comandas (no vincular a impresora de barra)
            - Especifica de productos: para todos los productos

        - Permitir añadir las mesas con el prefijo incluido

        - Poder indicar que una opcion de producto es de cocina / barra independientemente del producto
            - Opcion 1: Al seleccionar productos en la impresora especifica, mostrar tb las opciones
            - Opcion 2: En la propia configuración de la opción (prefiero)

        - Poder indicar número de comensales por mesa
        - Poder configurar un suplemento de mesa en base al número de comensales

        - La fecha de los pagos de la licencia por paypal esta mal
        - Pago de licencia: Desde otra vista: se queda recuperando información del pago
        - No se recupera el buffer de teclado en el precio del producto en todos los casos
        - Actualizacion de la version de android (en caliente, si que funciona tras reinicio)
            No sale la fecha de publicacion ni la nueva version
            No funciona el boton de actualizar

    WISHLIST:
        - Añadir campo de estilo 'style' a la BBDD de una printer, pero sin reflejo en el interfaz
            - Se aplica ad-hoc (por ejemplo bold)
            - ALTER TABLE `PRINTER` ADD `style` TEXT NULL DEFAULT NULL COMMENT 'Custom CSS rules to apply to html body' AFTER `copies`;
*/

/************************************************/
/* HASH CALCULATION                             */
/************************************************/

let _global_crcTable = null;

// https://stackoverflow.com/questions/18638900/javascript-crc32
export class CRC32 {
    private _crc = null;

    constructor(str){
        if (!str){
            str = '';
        }
        
        // build the CRC lookup table
        if (!_global_crcTable){
            _global_crcTable = [];
            for(let n=0; n < 256; n++) {
                let c = n;
                for(let k=0; k < 8; k++) {
                    c = ((c&1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1));
                }
                _global_crcTable[n] = c;
            }
        }

        // calculate the CRC value
        let crc = 0 ^ (-1);
        for (let i=0; i < str.length; i++ ) {
            crc = (crc >>> 8) ^ _global_crcTable[(crc ^ str.charCodeAt(i)) & 0xFF];
        }
        this._crc = (crc ^ (-1)) >>> 0;        
    }

    get value(){
        return this._crc;
    }
}

/************************************************/
/* CLIENT SESSION                               */
/************************************************/

interface ISession {
    device: string,
    session: string;
    token: string;
    user: User,
    place: Place;
    qrcode: QrCode
};

class Session implements ISession {
    private _session: ISession = {
        device: null,
        session: null,
        token: null,
        user: null,
        place: null,
        qrcode: null            
    }

    constructor(){
        // nothing to do
    }

    Clear(){
        this._session = {
            device: this._session.device,
            session: null,
            token: null,
            user: null,
            place: null,
            qrcode: null               
        }
    }

    /* getters */

    get serial(): string {      // device serial number is the CRC32 for the stored value
        if (this._session.place){
            return new CRC32(this._session.device + this._session.place.objid).value.toString(16).toUpperCase();
        }
        else {  // no place: this session comes from a client device
            if (this._session.qrcode){
                return new CRC32(this._session.qrcode.place.objid).value.toString(16).toUpperCase();
            }

            return "00000000";  // default value (this is an error in the session information)
        }
    }

    get device(): string  {
        return this._session.device;
    }

    set device(value: string){
        this._session.device = value;
    }

    get session(): string {
        return this._session.session;
    }

    get token(): string {
        return this._session.token;
    }

    get user(): User {
        return this._session.user;
    }

    get place(): Place {
        return this._session.place;
    }

    get qrcode(): QrCode {
        return this._session.qrcode;
    }

    /* setters */

    set session(value: string){
        this._session.session = value;
    }

    set token(value: string){
        this._session.token = value;
    }

    set user(value: User){
        this._session.user = value;
    }

    set place(value: Place){
        this._session.place = value;
    }

    set qrcode(value: QrCode){
        this._session.qrcode = value;
    }
}

/************************************/
/* PERIODIC REFRESH CONTROL         */
/************************************/

class MainClock {
    private _tickms = 250;

    private _refreshTick = new Subject<any>();
    public OnRefreshTick = this._refreshTick.asObservable();

    private _onesecondTick = new Subject<any>();
    public OnOneSecondTick = this._onesecondTick.asObservable();

    private _tensecondTick = new Subject<any>();
    public OnTenSecondTick = this._tensecondTick.asObservable();

    private _oneminuteTick = new Subject<any>();
    public OnOneMinuteTick = this._oneminuteTick.asObservable();

    private _clock_interval = null;
    constructor(){
        this._clock_interval = setInterval(() => {
            this._tick()
        }, this._tickms);
    }

    OnDestroy(){
        if (this._clock_interval){
            clearInterval(this._clock_interval);
        }
        this._clock_interval = null;
    }

    private _enabled = new Set();
    get Enable(){
        return (this._enabled.size == 0)
    }

    ClockEnable(source, value){
        if (value == false){    // add source for clock disable
            this._enabled.add(source);
        }
        else {                  // remove source for clock enable
            this._enabled.delete(source);
            if (this._enabled.size == 0){
                this._tick();
            }
        }
    }

    private _second_count = 0;
    private _tensec_count = 0;
    private _minute_count = 0;

    private _tick(){
        this._second_count++;
        if (this._second_count >= (1000 / this._tickms)){
            this._second_count = 0;
            this._onesecondTick.next();
            this._tensec_count++;
            this._minute_count++;
        }

        if (this._tensec_count >= 10){
            this._tensec_count = 0;
            this._tensecondTick.next();
        }

        if (this._minute_count >= 60){
            this._minute_count = 0;
            this._oneminuteTick.next();
        }

        let _skip = false;
        if (this.Enable){
            if (!_skip){
                _skip = true;
                this._refreshTick.next();
                _skip = false;
            }
        }
    }
}

/************************************/
/* PRIORITIZED PENDING UPDATES      */
/************************************/

class Pending {
    private _pending = [];

    /** ¿Hay un update del servidor para este objeto aun SIN APLICAR (en cola)? Lo usa
     *  el rescate de cobros: un eco que esta esperando turno no es un cobro perdido. */
    Has(table: string, objid?: any, uuid?: string): boolean {
        return this._pending.some(u => u && (u['table'] === table)
            && ((uuid && u['_uuid'] === uuid) || ((objid != null) && (u['objid'] == objid))));
    }
    
    private _refreshStage = new Subject<any> ();
    public OnRefreshStage = this._refreshStage.asObservable();

    private NotifyStage(){
        this._refreshStage.next(this._stage);
    }

    private _notify = false;
    set DoNotify(value){
        this._notify = value;
    }

    get Length(){
        return this._pending.length;
    }

    private _stage = 0;
    get Stage(){
        return this._stage;
    }

    set Stage(value){
        if (value == 0) {
            this.Reset();
        }

        let _next = Math.min(9, value);
        if (_next > this._stage){
            this._stage = _next

            this.NotifyStage();
            setTimeout(() => {
                this.NotifyPercent();
            }, 0);
        }
    }

    private _refreshPercent = new Subject<any> ();
    public OnRefreshPercent = this._refreshPercent.asObservable();

    /* total items progress */
    private _totalitm = 0;
    private _progritm = 0;

    /* active items progress */
    private _totalact: number = 0;
    private _progract: number = 0;

    /* old / past items progress */
    private _totalpst: number = 0;
    private _progrpst: number = 0;
    
    private LoadPcnt(progr, total){
        if ((this._stage == 0) || (!this._notify)){
            return 0;   // loading not started
        }

        if (this._stage >= 9){
            return 1;   // completelly loaded
        }

        return (total == 0) ? 0 : Math.floor((progr / total) * 100) / 100;
    }

    get LoadPercent(){
        let _percent = {
            tot: this.LoadPcnt(this._progritm, this._totalitm),
            act: this.LoadPcnt(this._progract, this._totalact),
            pst: this.LoadPcnt(this._progrpst, this._totalpst)    
        };

        return _percent;
    }

    private NotifyPercent(){
        if (!this._notify){
            return;     // notifications are disabled
        }

        this._refreshPercent.next(this.LoadPercent);
    }

    private get StageReady(){
        return (this.sync.LoadStage > 6);
    }

    private _blockProcessed = new Subject<any> ();
    public OnBlockProcessed = this._blockProcessed.asObservable();

    private _onblock_subscription = null;
    private _ProcessQueue(){
        let _stage = this.Stage;
        if (this._pending.length > 0){
            _stage = this._pending[0]['__priority'];
        }

        let _todo = Math.min(this._pending.length, this.StageReady ? 5000 : 500);
        this.sync.Clock.ClockEnable('queue', (_todo == 0) || this.StageReady);

        let _starttime = performance.now();  // max 50ms execution inside loop
        while ((_todo-- > 0) && ((performance.now() - _starttime) < 50)){
            let _oldstage = this.Stage;
            this.Dequeue(this._pending.shift());

            if (_oldstage != this.Stage){
                break;  // can change the max evaluation size
            }
        }

        this.sync.Clock.ClockEnable('queue', true);

        if ((this.Stage > 0) && (this._pending.length == 0)){
            this.Stage = 9;     // ensure that stage 9 is reached
        }

        // trigger block processed to continue with next block
        if (this._pending.length > 0){
            setTimeout(() => {
                this._blockProcessed.next();
            }, this.StageReady ? 0 : 50);
        }
        else {  // no more items, remove the subscription
            if (this._onblock_subscription != null){
                this._onblock_subscription.unsubscribe();
                this._onblock_subscription = null;
            }
        }
    }

    private ProcessQueue(){
        if (this._onblock_subscription == null){
            this._onblock_subscription = this.OnBlockProcessed.subscribe(
            data => {
                this._ProcessQueue();
            });
    
            this._blockProcessed.next();
        }
    }

    constructor(private sync: syncService){
        // nothing to do
    }

    OnDestroy(){
        if (this._onblock_subscription){
            this._onblock_subscription.unsubscribe();
            this._onblock_subscription = null;
        }
    }

    /*********************************/
    /* LOAD PROGRESS                 */
    /*********************************/

    Reset(){
        this._totalitm = this._progritm = 0;
        this._totalact = this._progract = 0;
        this._totalpst = this._progrpst = 0;

        this._stage = 0;
        this._pending = [];

        this.NotifyStage();
    }

    private IsActive(_item){
        return _item['__priority'] < 7;
    }

    private Enqueue(_items){
        this._totalitm += _items.length;

        for(let _item of _items){
            this.IsActive(_item) ? this._totalact++ : this._totalpst++;
        }
    }

    private Dequeue(_item){
        let _update = false; 

        let _tot_pcnt = [ 0, 0 ];
        let _act_pcnt = [ 0, 0 ];
        let _pst_pcnt = [ 0, 0 ];

        // total items counter
        _tot_pcnt[0] = this.LoadPcnt(this._progritm, this._totalitm);
        this._progritm++;
        _tot_pcnt[1] = this.LoadPcnt(this._progritm, this._totalitm);

        // active items counter
        if (this.IsActive(_item)){
            _act_pcnt[0] = this.LoadPcnt(this._progract, this._totalact);
            this._progract++;
            _act_pcnt[1] = this.LoadPcnt(this._progract, this._totalact);
        }

        // past items counter
        else {
            _pst_pcnt[0] = this.LoadPcnt(this._progrpst, this._totalpst);
            this._progrpst++;
            _pst_pcnt[1] = this.LoadPcnt(this._progrpst, this._totalpst);
        }

        this.sync.DoRefresh(_item);
        
        _update = _update || (_tot_pcnt[1] > _tot_pcnt[0])
        _update = _update || (_act_pcnt[1] > _act_pcnt[0])
        _update = _update || (_pst_pcnt[1] > _pst_pcnt[0])

        this.Stage = parseInt(_item['__priority']);
        if (_update){
            this.NotifyPercent();
        }
    }

    /*********************************/
    /* CONCAT UPDATES                */
    /*********************************/

    protected mysqlToDateStr(date){
        return date ? date.replace(' ', 'T') + '.000Z' : null;
    }    

    private _insert_recent(_recent, table, objid){
        if (!_recent[table]){
            _recent[table] = new Set();
        }
        _recent[table].add(objid);
    }

    private _build_recents(_changes){
        let _recent : {[key: string]: Set<any>} = {};

        // obtain the recent tickets with the related session
        if ('TICKET' in _changes){
            for(let _ticket of _changes['TICKET']){
                let _date = new Date(Date.parse(this.mysqlToDateStr(_ticket['updated'])));
                if (_date.getTime() > ((new Date()).getTime() - AppConstants.recentMs)){
                    this._insert_recent(_recent, 'TICKET', _ticket['objid']);       // add the recent ticket entry
                    this._insert_recent(_recent, 'SESSION', _ticket['session']);    // add the related session entry
                } 
            }    

            // obtain the related entries
            let _related = [ 'TICKETPRODUCT', 'TICKETOPTION', 'TICKETEVENT', 'TICKETOFFER', 'TICKETEXTRA', 'TICKETINVOICE', 'TICKETDISCOUNT', 'TICKETAUDIT', 'TICKETCHANGE', 'TICKETBAI', 'VERIFACTU', 'TICKETSII' ];
            for(let _table of _related){
                // invoice: invoice.ticket == ticket.objid
                if ((_table == 'TICKETINVOICE') && (_table in _changes) && ('TICKET' in _recent)){           
                    for(let _item of _changes[_table]){
                        if (_recent['TICKET'].has(_item['ticket'])){
                            this._insert_recent(_recent, _table, _item['objid']);
                        }
                    }
                }

                // ticketchange: tikcetchange.ticket == ticket.objid
                if ((_table == 'TICKETCHANGE') && (_table in _changes) && ('TICKET' in _recent)){           
                    for(let _item of _changes[_table]){
                        if (_recent['TICKET'].has(_item['ticket'])){
                            this._insert_recent(_recent, _table, _item['objid']);
                        }
                    }
                }

                // ticketbai: ticketbai.change == ticketchange.objid            
                if ((_table == 'TICKETBAI') && (_table in _changes) && ('TICKETCHANGE' in _recent)){      
                    for(let _item of _changes[_table]){
                        if (_recent['TICKETCHANGE'].has(_item['ticketbai'])){
                            this._insert_recent(_recent, _table, _item['objid']);
                        }
                    }                
                }                

                // verifactu: verifactu.change == ticketchange.objid            
                if ((_table == 'VERIFACTU') && (_table in _changes) && ('TICKETCHANGE' in _recent)){      
                    for(let _item of _changes[_table]){
                        if (_recent['TICKETCHANGE'].has(_item['verifactu'])){
                            this._insert_recent(_recent, _table, _item['objid']);
                        }
                    }                
                }                

                // ticketsii: ticketsii.change == ticketchange.objid            
                if ((_table == 'TICKETSII') && (_table in _changes) && ('TICKETCHANGE' in _recent)){      
                    for(let _item of _changes[_table]){
                        if (_recent['TICKETCHANGE'].has(_item['ticketsii'])){
                            this._insert_recent(_recent, _table, _item['objid']);
                        }
                    }                
                }   

                // ticketproduct: ticketproduct.ticket == ticket.objid
                if ((_table == 'TICKETPRODUCT') && (_table in _changes) && ('TICKET' in _recent)){     
                    for(let _item of _changes[_table]){
                        if (_recent['TICKET'].has(_item['ticket'])){
                            this._insert_recent(_recent, _table, _item['objid']);
                        }
                    }                
                }

                // ticketoption: ticketoption.product == ticketproduct.objid            
                if ((_table == 'TICKETOPTION') && (_table in _changes) && ('TICKETPRODUCT' in _recent)){      
                    for(let _item of _changes[_table]){
                        if (_recent['TICKETPRODUCT'].has(_item['product'])){
                            this._insert_recent(_recent, _table, _item['objid']);
                        }
                    }                
                }

                // ticketoffer: ticketoffer.ticket == ticket.objid
                if ((_table == 'TICKETOFFER') && (_table in _changes) && ('TICKET' in _recent)){          
                    for(let _item of _changes[_table]){
                        if (_recent['TICKET'].has(_item['ticket'])){
                            this._insert_recent(_recent, _table, _item['objid']);
                        }
                    }                                
                }

                // ticketextra: ticketextra.ticket == ticket.objid
                if ((_table == 'TICKETEXTRA') && (_table in _changes) && ('TICKET' in _recent)){       
                    for(let _item of _changes[_table]){
                        if (_recent['TICKET'].has(_item['ticket'])){
                            this._insert_recent(_recent, _table, _item['objid']);
                        }
                    }                                
                }

                // ticketdiscount: ticketdiscount.ticket == ticket.objid
                if ((_table == 'TICKETDISCOUNT') && (_table in _changes) && ('TICKET' in _recent)){       
                    for(let _item of _changes[_table]){
                        if (_recent['TICKET'].has(_item['ticket'])){
                            this._insert_recent(_recent, _table, _item['objid']);
                        }
                    }                                
                }

                // ticketmixedpay: ticketmixedpay.ticket == ticket.objid
                if ((_table == 'TICKETMIXEDPAY') && (_table in _changes) && ('TICKET' in _recent)){     
                    for(let _item of _changes[_table]){
                        if (_recent['TICKET'].has(_item['ticket'])){
                            this._insert_recent(_recent, _table, _item['objid']);
                        }
                    }                
                }

                // ticketaudit: ticketaudit.ticket == ticket.objid
                if ((_table == 'TICKETAUDIT') && (_table in _changes) && ('TICKET' in _recent)){       
                    for(let _item of _changes[_table]){
                        if (_recent['TICKET'].has(_item['ticket'])){
                            this._insert_recent(_recent, _table, _item['objid']);
                        }
                    }                                
                }
            }
        }

        return _recent;
    }

    private _filter_recents(_updates){
        let _changes = {};

        for(let _change of _updates){
            let _table = _change['table'];
            if (!(_table in _changes)){
                _changes[_table] = [];
            }
            _changes[_table].push(_change);
        } 

        return this._build_recents(_changes);
    }

    private _isrecent(_update, _recent){
        return (_update['table'] in _recent) && (_recent[_update['table']].has(_update['objid']));
    }

    private _priority(_update, _recents){
        // Datáfono boot-first override: the floorplan tables (QRCODE,
        // DRAWITEM, PLACEAREA) must hydrate before anything else so
        // the mesas grid can render at "PLACE rendered + ~1s" instead
        // of waiting for the whole catalog to finish at stage 3.
        // Priority 1.8 slots them right after PLACE (1.5) and before
        // the first catalog table at stage 3.x. Combined with the
        // ApplicationRef.tick() fired in ui-place on the stage > 3
        // transition, mesas become visible almost immediately after
        // the IndexedDB cache read completes.
        if (this.sync.IsDatafono){
            switch(_update['table']){
                case 'QRCODE':
                case 'DRAWITEM':
                case 'PLACEAREA':
                    return 1.8;
            }
        }

        // update synchronously
        switch(_update['table']){
            case 'SESSION':
                if (this.sync.Session.session == _update['id']){
                    return 1.1;
                }
                break;

            case 'FCM': return 1.2;

            case 'USER': return 1.3;
            case 'STAFF': return 1.4;

            case 'PLACE': 
                if (!this.sync.Session.place){
                    return 1.5;
                }
                break;
        }    

        // instances without graphical component
        switch(_update['table']){
            case 'ADDRESS': return 2.1;
            case 'STRIPE': return 2.1;
            case 'RASPPI': return 2.1;
            case 'USERADDRESS': return 2.2;
            case 'PLACEOPT': return 2.2;
            case 'PLACELINK': return 2.2;
            case 'INVOICEBUSINESS': return 2.2;
            case 'INVOICEMAIL': return 2.2;
            case 'ACCOUNTINVOICE': return 2.3;
            case 'PAYACCOUNT': return 2.4;
            case 'CASHCHANGE': return 2.5;
            case 'AUDITINFO': return 2.6;
            case 'AUDITTILL': return 2.6;
            case 'AUDIT': return 2.7;
            case 'INVOICEITEM': return 2.7;
            case 'INVOICEDATA': return 2.8;
        }

        // first order instances
        switch(_update['table']){
            case 'DRAWITEM':
                return 3.1;

            case 'PLACEAREA':
            case 'RASPPICONNECT':
                return 3.2;
    
            case 'QRCODE':
            case 'PRINTER':
            case 'SCALE':
            case 'PAYTERMINAL':
                if (_update.status != 'DE'){
                    return 3.3;
                }
                break;
    
            case 'OFFERPRODUCTOPT':
            case 'EXTRAPRODUCTOPT':
            case 'PRINTERPRODUCTOPT':
                    return 3.4;

            case 'PRODUCTOPT':
            case 'OFFERPRODUCT': 
            case 'OFFERPERIOD': 
            case 'DISCOUNTPERIOD':
            case 'EXTRATABLE': 
            case 'EXTRAPRODUCT':
            case 'EXTRAPERIOD':
            case 'PRINTERPRODUCT': 
                return 3.5;

            case 'CATEGORYDEP':
            case 'PRESELECTOPT':
                return 3.6;
    
            case 'CATEGORY':
            case 'PRESELECT':
                return 3.7;

            case 'PRODUCT':
            case 'OFFER':
            case 'EXTRA':
            case 'DISCOUNT':
                return 3.8;
        }

        // product families
        switch(_update['table']){
            case 'FAMILYPERIOD':
            case 'FAMILYPRODUCT':
                return 4.1;

            case 'FAMILY':
                return 4.2;
        }

        // active tickets & members
        switch(_update['table']){
            case 'TICKETOFFER': 
                if (this._isrecent(_update, _recents)) {
                    return 5.1; 
                }
                break;
            case 'TICKETEXTRA': 
                if (this._isrecent(_update, _recents)) {
                    return 5.1; 
                }
                break;
            case 'TICKETOPTION': 
                if (this._isrecent(_update, _recents)) {
                    return 5.3; 
                }
                break; 
            case 'TICKETMIXEDPAY':
                if (this._isrecent(_update, _recents)) {
                    return 5.3;     
                }
                break;
            case 'TICKETBAI': 
                if (this._isrecent(_update, _recents)) {
                    return 5.4;     
                }
                break;
            case 'VERIFACTU': 
                if (this._isrecent(_update, _recents)) {
                    return 5.4;     
                }
                break;
            case 'TICKETSII': 
                if (this._isrecent(_update, _recents)) {
                    return 5.4;     
                }
                break;
            case 'TICKETPRODUCT': 
                if (this._isrecent(_update, _recents)) {
                    return 5.4; 
                }
                break;                
            case 'TICKETDISCOUNT': 
                if (this._isrecent(_update, _recents)) {
                    return 5.4; 
                }
                break;                
            case 'TICKETCHANGE': 
                if (this._isrecent(_update, _recents)) {
                    return 5.5;     
                }
                break;
            case 'TICKETINVOICE': 
                if (this._isrecent(_update, _recents)) {
                    return 5.5;     
                }
                break;                
            case 'TICKETAUDIT': 
                if (this._isrecent(_update, _recents)) {
                    return 5.5;     
                }
                break;                
            case 'SESSION': 
                if (this._isrecent(_update, _recents)) {
                    return 5.6;     
                }
                break;                
            case 'TICKET': 
                if (this._isrecent(_update, _recents)) {
                    return 5.7; 
                }
                break;
        }

        // waiter requests
        switch(_update['table']){
            case 'ASKWAITER':
                if (_update['status'] == 'AC'){
                    return 5.9;
                }
                break;
        }
        
        // place information (this is a place update)
        switch(_update['table']){
            case 'QRCODE':
            case 'PRINTER':
            case 'SCALE':
            case 'PAYTERMINAL':
                return 6.1;

            case 'PAYMENT': return 6.3;
            case 'PLACE': return 6.4;
        }

        // older tickets & members
        switch(_update['table']){
            case 'QRCODE': return 8.1;
            case 'TICKETOFFER': return 8.1;
            case 'TICKETEXTRA': return 8.1;
            case 'TICKETDISCOUNT': return 8.1;
            case 'TICKETOPTION': return 8.3;
            case 'TICKETMIXEDPAY': return 8.3;
            case 'TICKETPRODUCT': return 8.4;
            case 'TICKETBAI': return 8.4;
            case 'VERIFACTU': return 8.4;
            case 'TICKETSII': return 8.4;
            case 'TICKETAUDIT': return 8.4;
            case 'ASKWAITER': return 8.5;
            case 'TICKETINVOICE': return 8.5;
            case 'TICKETCHANGE': return 8.5;
            case 'SESSION': return 8.6;
            case 'TICKET': return 8.7;
        }

        // other instance information
        console.warn("WARNING: table '" + _update['table'] + "' not prioritized!");
        return 9;
    }

    private _ChangeKey(change){
        return change['table'] + '@' + change['objid'];
    }

    private _MergeChange(chng1, chng2){
        let _updated1 = new Date(Date.parse(chng1['updated'].replace(' ', 'T') + '.000Z'));
        let _updated2 = new Date(Date.parse(chng2['updated'].replace(' ', 'T') + '.000Z'));

        if (_updated1.getTime() > _updated2.getTime()){
            return Object.assign(chng2, chng1);     // source is the recent item
        }
        else {
            return Object.assign(chng1, chng2);     // source is the recent item
        }
    }

    SortUpdates(_updates){
        for(let _update of _updates){
            _update['__priority'] = this._priority(_update, {});
        }

        _updates.sort((a, b) => {
            return (a['__priority'] - b['__priority']);
        });

        return _updates;
    }

    async Concat(_updates, _synchronous){
        await new Promise(resolve => setTimeout(resolve, 0));   // release the event loop

        if (_updates.length > 0){
            let _brief = [];    // only for debugging purposes

            // set the priority for all updates
            let _recents = this._filter_recents(this._pending.concat(_updates));
            for(let _update of _updates){
                _update['__priority'] = this._priority(_update, _recents);

                // reprioritize inserted entries (in range [-9 .. -1])
                if (_update['_actn'] == 'do_insert'){
                    _update['__priority'] -= 10.0;
                }

                if (!(_update['table'] in _brief)){
                    _brief[_update['table']] = 0;
                }
                _brief[_update['table']]++;
            }

            _updates.sort((a, b) => {
                return (a['__priority'] - b['__priority']);
            });

            // merge the udpates in the pending list
            if (this._pending.length > 0){
                let _pndmap = new Map();
                for(let _pending of this._pending){
                    _pndmap.set(this._ChangeKey(_pending), _pending);
                }
    
                for(let _idx = _updates.length - 1; _idx >= 0; _idx--){
                    let _update = _updates[_idx];
                    let _pnditm = _pndmap.get(this._ChangeKey(_update));
                    let _assign = null;   

                    if (_pnditm){
                        _assign = this._MergeChange(_update, _pnditm);      // merge this entry;    
                        _pndmap.set(this._ChangeKey(_update), _assign);     // replace this entry   
                        _updates.splice(_idx, 1);                           // remove from updates
                    }
                }
    
                this._pending = [..._pndmap.values()];
            }

            // enqueue all other updates (not in pending list)
            this.Enqueue(_updates);

            // release the synchronous entries      
            if (_updates.length > 0){
                this.sync.Clock.ClockEnable('synchronous', this.StageReady);
                while(_updates.length > 0){
                    if ((!_synchronous) && (this.Stage > 3)) {
                        break;
                    }

                    this.Dequeue(_updates.shift());
                }
                this.sync.Clock.ClockEnable('synchronous', true);
            }
            
            if (_updates.length > 0){
                this._pending = this._pending.concat(_updates);

                this._pending.sort((a, b) => {
                    return (a['__priority'] - b['__priority']);
                });
            }

            this.ProcessQueue();
        }
    }
}

/************************************/
/* CONNECTED INFO                   */
/************************************/

class ConnectedServer {
    private _isconnected = false;

    // Último resultado de hablar con el servidor (true = respondió). Lo usa el rescate
    // de cobros sin confirmar para no reintentar ni contar intentos estando sin red.
    get IsConnected(): boolean {
        return this._isconnected;
    }

    private _doCancelUrl = 'model/conn/cancel.php';
    private _doCreateUrl = 'model/conn/create.php';
    private _doDeleteUrl = 'model/conn/delete.php';
    private _doUpdateUrl = 'model/conn/update.php';

    private _triggers = null;

    private _trgrcust = {};

    AddTriggers(triggers){
        for(let _key in triggers){
            this._trgrcust[_key] = triggers[_key];
        }
    }

    DelTriggers(triggers){
        for(let _key in triggers){
            delete(this._trgrcust[_key]);
        }
    }
    
    get Triggers(){
        if (this.sync.Session.qrcode && this.sync.Session.qrcode.objid){
            let _triggers = {
                QRCODE: this.sync.Session.qrcode.objid,
                PLACE: this.sync.Session.qrcode.place.objid,
            };

            return Object.assign({}, this._trgrcust, _triggers);
        }

        if (this.sync.Session.place && this.sync.Session.place.objid){
            let _triggers = {
                PLACE: this.sync.Session.place.objid,
                USER: []    // will be completed below
            }

            // include the place user updates
            if (this.sync.Session.place.user){
                _triggers['USER'].push(this.sync.Session.place.user.objid);
            }

            // include the place waiter users updates
            for(let _waiter of this.sync.Session.place.waiters){
                if (_waiter.IsValid){
                    _triggers['USER'].push(_waiter.user.objid);
                }
            }

            return Object.assign({}, this._trgrcust, _triggers);
        }

        if (this.sync.Session.user && this.sync.Session.user.objid){
            let _triggers = {
                USER: this.sync.Session.user.objid
            }

            return Object.assign({}, this._trgrcust, _triggers);
        }

        // default: create a connection with the session
        return {
            SESSION: this.sync.Session.session
        };
    }

    private _onExpired = new Subject<any>();
    private OnExpired = this._onExpired.asObservable();

    private _expired_subscription = null;
    constructor(private sync: syncService){
        this._expired_subscription = this.OnExpired.subscribe(
        data => {
            this.sync._OnSessionExpired();
        });
    }

    OnDestroy(){
        if (this._expired_subscription){
            this._expired_subscription.unsubscribe();
            this._expired_subscription = null;
        }
    }

    CreateConnection(){
        return new Promise((resolve) => {
            let _triggers = this.Triggers;

            if (!this._isconnected && _triggers){
                this._isconnected = true;

                // Timeout 12s: CreateConnection se espera (await) dentro de
                // _DoRefresh -> UpdateConnection ANTES de mandar la comanda. Sin
                // acotarlo, un conn/create colgado en reconexion medio-abierta
                // bloquea el commit igual que el propio synchronize.
                this.sync.DoRequest(this._doCreateUrl, null, _triggers, false, null, null, null, 12000)
                .then(data => {
                    if (data['errorcode'] == 0){
                        this._triggers = _triggers;
                        console.info("[CONNECTED] Connected to server (id: " + data['connection'] + ")");
                    }
                    else {
                        if ((data['errorcode'] == 2) || (data['errorcode'] == 3)){
                            this._onExpired.next();
                        }
                        else {
                            console.error("[CONNECTED] Error [" + data['errorcode'] + "] creating connection");
                        }
                    }

                    this._isconnected = (data['errorcode'] == 0);
                    resolve(this._isconnected);
                }, error => {
                    // Revertir el _isconnected optimista puesto arriba. Sin esto, un
                    // fallo/timeout de conn/create deja al cliente creyendo que hay
                    // conexion: UpdateConnection no reintenta el create (guard
                    // !this._isconnected) y _UpdateConnection devuelve exito fantasma
                    // (resolve(!_toupdate && this._isconnected)). Con los timeouts
                    // acotados este camino de error ahora SI se alcanza (antes colgaba).
                    this._isconnected = false;

                    if (error.status != 0){
                        console.error("[REQUEST] Error [" + error.status + "] in http request '" + this._doCreateUrl + "'");
                    }

                    resolve(false);
                });
            }
            else {
                resolve(false);
            }
        });
    }

    DeleteConnection(){
        return new Promise((resolve) => {
            if (this._isconnected){
                this._isconnected = false;

                this.sync.DoRequest(this._doDeleteUrl, null, null, false, null, null, null, 12000)
                .then(data => {
                    if (data['errorcode'] == 0){
                        console.info("[CONNECTED] Disconnected from service")
                    }
                    else {
                        console.error("[CONNECTED] Error [" + data['errorcode'] + "] deleting connection");
                    }

                    this._triggers = null;
                    
                    this._isconnected = !((data['errorcode'] == 0) || (data['errorcode'] == 2))
                    resolve(!this._isconnected);
                }, error => {
                    if (error.status != 0){
                        console.error("[REQUEST] Error [" + error.status + "] in http request '" + this._doDeleteUrl + "'");
                    }
    
                    resolve(false);
                });        
            }
            else {
                resolve(false);
            }
        });
    }

    private _UpdateConnection(timestamp){
        let _triggers = this.Triggers;

        return new Promise((resolve) => {
            let _toupdate = (JSON.stringify(this._triggers) != JSON.stringify(_triggers));
            if (this.sync._started && this._isconnected && _toupdate){
                this.sync.DoRequest(this._doUpdateUrl, { timestamp: timestamp }, _triggers, false, null, null, null, 12000)
                .then(data => {
                    if (data['errorcode'] == 0){
                        this._triggers = _triggers;
                        console.info("[CONNECTED] Server configuration updated..");
                        resolve(true);
                    }
                    else {
                        console.error("[CONNECTED] Error [" + data['errorcode'] + "] updating service");
                    }
                }, error => {
                    if (error.status != 0){
                        console.error("[REQUEST] Error [" + error.status + "] in http request '" + this._doUpdateUrl + "'");
                    }
    
                    resolve(false);
                });        
            }
            else {
                resolve(!_toupdate && this._isconnected);   // nothing to update
            }
        });    
    }

    async UpdateConnection(timestamp){
        if (this.sync._started){
            if (!this.Triggers){
                await this.DeleteConnection();
            }
    
            if (!this._isconnected){
                await this.CreateConnection();
            }
        }

        return this._UpdateConnection(timestamp);     // both connected and configured: do the update
    }

    CancelRefresh(){
        return new Promise((resolve) => {
            if (this._isconnected){
                this.sync.DoRequest(this._doCancelUrl, null, null, false, null, null, null, 12000)
                .then(data => {
                    let _success = (data['errorcode'] == 0);
                    if (!_success){
                        console.error("[CONNECTED] Error [" + data['errorcode'] + "] sending cancelation");
                    }

                    resolve(_success);
                }, error => {
                    if (error.status != 0){
                        console.error("[REQUEST] Error [" + error.status + "] in http request '" + this._doCancelUrl + "'");
                    }

                    resolve(false);
                });
            }  

            resolve(false)  // not connected
        });
    }
}

/************************************/
/* EXTERNAL STORAGE                 */
/************************************/

class ExternalStore {
    private _conndata = {
        host: null,
        port: null,
        name: null
    };

    private _wsstorage = null;

    constructor(private sync: syncService){
        let _place = this.sync.Session.place
        if (_place) {
            this.GetStorage();
        }
    }

    OnDestroy(){
        if (this._wsstorage){
            this._wsstorage.OnDestroy();
            this._wsstorage = null;
        }
    }

    async GetStorage(){
        let _till = this.sync.Session?.place?.till;
        if (!_till){
            return null;    // not suitable
        }

        let _name = _till.name;
        let _host = _till.wshost;
        let _port = _till.wsport;

        let _reuse = (this._wsstorage != null);

        if (this._conndata.host != _host){
            _reuse = false;
        }

        if (this._conndata.port != _port){
            _reuse = false;
        }

        if (this._conndata.name != _name){
            _reuse = false;
        }

        if (_reuse){
            return this._wsstorage;
        }
        else {
            let _place = this.sync.Session.place

            if (this._wsstorage){
                this._wsstorage.OnDestroy();
                this._wsstorage = null;
            }

            this._wsstorage = await this.sync.devsvc.WSStorage(this.sync);
            if (this._wsstorage){
                _place.AllowExternalStore = _till.IsLocal;

                this._conndata.host = _host;
                this._conndata.port = _port;
                this._conndata.name = _name;

                let _subscription = this._wsstorage.OnDisconnected.subscribe(
                data => {   // release on disconnect
                    _place.AllowExternalStore = false;

                    _subscription.unsubscribe();
                    if (this._wsstorage){
                        this._wsstorage.OnDestroy();
                        this._wsstorage = null;
                    }
                });                  
            }
            else {
                _place.AllowExternalStore = false;
            }

            return this._wsstorage;
        }
    }
}

/************************************/
/* SYNCHRONIZATION SERVICE          */
/************************************/

@Injectable()
export class syncService implements OnDestroy {
    private _doRefreshUrl = 'model/synchronize.php';

    /*
        LOGIN: Access to POS application (owner / waiter)
        GUEST: Access through QR code in table
        DEVRY: Access through web for home delivery 
    */

    private _access: string = null;     // 'LOGIN', 'GUEST', 'DEVRY' 
    get Access() {
        return this._access;
    }

    set Access(value){
        this._access = value;
        console.info("[INITIALIZE] Setting access mode to: '"  + this._access + "'")
    }

    private _serverversion: any = null;
    get ServerVersion(){
        return this._serverversion;
    }

    SetServerVersion(version, restored){
        return new Promise((resolve) => {
            if (this._serverversion != version){
                this.store.CheckObsoleteData(version, restored).then(
                data => {
                    this._serverversion = version;
                    resolve(true);  // updated
                });
            }
            else {
                resolve(false);     // not updated
            }    
        })
    }

    private _session: Session = new Session();
    get Session() : Session {
        return this._session;
    }

    get _started(){
        return (this.Session?.session != null);
    }

    private _timestamp = 0;
    private _reference = 0;
    
    // Track page visibility to detect device lock/unlock
    private _pageHiddenTimestamp: number = null;
    private _visibilityChangeHandler: () => void = null;
    private _pagehideHandler: (event: PageTransitionEvent) => void = null;
    private _pageshowHandler: (event: PageTransitionEvent) => void = null;

    private _clock: MainClock = null;
    get Clock(){
        if (!this._clock){
            this._clock = new MainClock();
        }
        return this._clock;
    }

    private _pending: Pending = null;
    private get Pending(){
        if (!this._pending){
            this._pending = new Pending(this);
        }
        return this._pending;
    }

    get LoadStage(){
        return this.Pending.Stage;
    }

    private _stage_target = null;
    get LoadTarget(){
        if (this._stage_target == null){
            let _target = null;

            if (!_target && this._session.place && this._session.place.objid){
                _target = 'PLACE';
            }    
    
            if (!_target && this._session.qrcode && this._session.qrcode.objid){
                _target = 'QRCODE';
            }    
    
            if (!_target && this._session.device && this._session.session){
                _target = 'SESSION';
            }  

            this._stage_target = _target;
        }
        return this._stage_target;
    }

    private _connected: ConnectedServer = null;
    get Connected(){
        if (!this._connected){
            this._connected = new ConnectedServer(this);
        }
        return this._connected;
    }

    private _wsstorage: ExternalStore = null;
    get wsstore(){
        if (!this._wsstorage){
            this._wsstorage = new ExternalStore(this);
        }
        return this._wsstorage;
    }

    /**********************************/
    /* PUBLIC OBSERVERS               */
    /**********************************/
   
    get LoadPercent(){
        return this.Pending.LoadPercent;
    }

    get OnRefreshStage(){
        return this.Pending.OnRefreshStage;
    }

    get OnRefreshPercent(){
        return this.Pending.OnRefreshPercent;
    }

    /**********************************/
    /* BACKGROUND REFRESH CONTROL     */
    /**********************************/

    private _refresh_timeout = null;
    private async _initializeRefresh(){
        if (this._rfrstatus == 'EXPIRED'){
            this._rfrstatus = 'STOPPED';
        }

        this._timestamp = 0;

        console.info("[INITIALIZE] Connected to data service");
        await this._resumeRefresh();
    }

    private _repeatRefresh(){
        if (this._refresh_timeout){
            clearTimeout(this._refresh_timeout);
            this._refresh_timeout = null;
        }

        // On a datáfono the CPU is tight and the information pulled by
        // the refresh loop (tickets, table state) doesn't need sub-second
        // latency for the cashier flow. Multiply the base 500ms by 4 →
        // ~2s, which still feels responsive and drops the sync loop cost
        // to a quarter.
        let _delay = AppConstants.MainRefresh;
        if (this.IsDatafono){
            _delay = _delay * 4;
        }
        // Sin red: no spamear synchronize ni la consola — la reconexion la
        // dispara http.OnOnline -> _retryAfterWake de inmediato.
        if (!this.http.IsOnline){
            _delay = 15000;
        }

        this._refresh_timeout = setTimeout(() => {
            if (this._rfrstatus == 'RUNNING') {
                if (!this._onrefresh){
                    // reset=true cuando el watchdog marco un reintento del
                    // arranque (la carga inicial se colgo y se cancelo). Se
                    // difiere aqui, al unico _DoRefresh que lanza el loop, para
                    // no solapar refreshes (ver comentario en el watchdog).
                    let _bootReset = this._pendingBootReset;
                    this._pendingBootReset = false;
                    this._DoRefresh(_bootReset).then(
                    data => {
                        this._repeatRefresh();
                    },
                    error => {
                        // Sin este handler de RECHAZO el loop moria: si _DoRefresh
                        // rechaza (una excepcion TS al procesar updates, un update
                        // malformado, etc.) el .then de exito no corre, _repeatRefresh
                        // NO se relanza y el setTimeout del loop no se reprograma —
                        // ni el watchdog (vive aqui dentro). El movil dejaba de
                        // sincronizar (mesas) hasta recargar. Reprogramamos siempre.
                        // Limpieza por el mismo camino que la cancelacion logica:
                        // _cancelInflightRefresh libera _onrefresh, rescata los writes
                        // en vuelo y EMITE _refreshCompleted — asi no dejamos colgado
                        // a ningun waiter de OnRefreshCompleted (_ForceRefresh /
                        // _CancelOngoing). Cualquier XHR viejo lo descarta el guard
                        // de generacion.
                        console.error("[SERVICE] _DoRefresh rechazo — el loop continua (sin esto se paraba la sync hasta recargar)", error);
                        this._cancelInflightRefresh('refresh-error');
                        this._repeatRefresh();
                    });
                }
                else {
                    // Watchdog de refresh zombie: un long-poll sano nunca
                    // supera ~30s (20s de espera del server + latencia). Si
                    // _onrefresh lleva mas del umbral es que iOS mato el XHR
                    // en un lock CORTO (<60s, que no dispara el wake retry)
                    // o la red se lo trago. Sin este check, _onrefresh
                    // quedaba true para siempre y el loop tickeaba sin
                    // refrescar hasta el siguiente lock largo o reload.
                    //
                    // Backoff: en browser NO hay timeout HTTP efectivo
                    // (Angular ignora la opcion timeout de http.ts) — una
                    // respuesta legitima pero MUY lenta (delta enorme + red
                    // mala) que tarde >45s sostenidamente entraria en bucle
                    // de cancelacion sin progresar jamas (el _timestamp no
                    // avanza y el delta solo crece). Cada cancelacion
                    // consecutiva duplica el umbral (45s → 90s → 180s cap)
                    // para dar hueco a que la respuesta lenta complete; un
                    // refresh exitoso resetea los strikes.
                    const _threshold = 45000 * Math.min(Math.pow(2, this._watchdogStrikes), 4);
                    if (Date.now() - this._refreshStartedAt > _threshold){
                        console.info("[SERVICE] refresh en vuelo >" + (_threshold / 1000) + "s — watchdog cancela el XHR zombie (strike " + (this._watchdogStrikes + 1) + ")");
                        this._watchdogStrikes = Math.min(this._watchdogStrikes + 1, 2);
                        this._cancelInflightRefresh('watchdog');

                        // Arranque: el XHR cancelado ERA la carga inicial
                        // (SESSION/PLACE) y su .then —el que avanza LoadStage—
                        // se descarta con el generation++ de la cancelacion.
                        // Sin relanzarla, el arranque queda clavado en
                        // "inicializando" hasta que el usuario cierra y reabre
                        // (mismo peligro que evita el GUARD arranque de
                        // _CommitChanges, que el watchdog no tenia). Marcamos
                        // reset para el PROXIMO refresh que lance el propio loop
                        // (abajo, en la rama !_onrefresh), en vez de llamar
                        // _DoRefresh(true) aqui: _DoRefresh no pone _onrefresh a
                        // true hasta despues de UpdateConnection (hasta ~12s),
                        // asi que lanzarlo aqui + el _repeatRefresh de abajo
                        // podian colar dos refreshes en vuelo a la vez.
                        if (!this._stageready_notified){
                            console.info("[SERVICE] watchdog en arranque — marcando reintento de la carga inicial");
                            this._pendingBootReset = true;
                        }
                    }
                    this._repeatRefresh();
                }
            }
        }, _delay);
    }

    private _rfrstatus = 'STOPPED';
    private _onrefresh = false;
    // Timestamp de arranque del refresh en vuelo — lo consumen el watchdog
    // de _repeatRefresh y (indirectamente) _retryAfterWake para distinguir
    // un long-poll sano (siempre <30s) de un XHR zombie.
    private _refreshStartedAt = 0;
    // Cancelaciones consecutivas del watchdog sin refresh exitoso entre
    // medias — escala el umbral (45s/90s/180s) para no entrar en bucle
    // contra una respuesta legitima pero muy lenta.
    private _watchdogStrikes = 0;
    // El watchdog lo marca cuando cancela la carga inicial del arranque; el
    // proximo _DoRefresh del loop lo consume para relanzarla con reset=true
    // sin solapar refreshes (ver watchdog en _repeatRefresh).
    private _pendingBootReset = false;

    /** Cancelacion logica del XHR en vuelo: generation++ (sus handlers
     *  .then/.error/.catch haran no-op) + RESCATE de los writes que
     *  _prepare_syncronize movio a _sendchanges — el guard de generation
     *  descarta el _onfailure_synchronize del zombie, que era el unico
     *  camino que los re-encolaba. El rescate descarta los writes obsoletos
     *  (los que ya tienen una version mas nueva encolada) para no re-marcar
     *  estado viejo. Devuelve true si habia refresh que cancelar. */
    private _cancelInflightRefresh(reason: string){
        if (!this._onrefresh){
            return false;
        }

        console.info("[SESSION] XHR en vuelo cancelado (" + reason + ") — generation " + this._refreshGeneration + " → " + (this._refreshGeneration + 1));
        this._refreshGeneration++;
        // OJO Copilot / NO re-tocar: el bump de _wakeGeneration lo hace SOLO el
        // wake handler (_retryAfterWake), NO aquí. Solo el WAKE (iOS sleep/bfcache/
        // network-online) es el zombie que clobberea (facturas-sin-líneas). Un
        // supersede por 'commit', 'watchdog' o 'refresh-error' NO es zombie: su
        // respuesta READ es válida y hay que APLICARLA (instantáneo de 2.0.2.8).
        // Una corrección de Copilot (#1768 2ª tanda) subió _wakeGeneration aquí
        // para TODO reason != 'commit' -> descartaba el poll del receptor superado
        // por un refresh-error (jitter de red) justo al llegar el push -> el cambio
        // volvía a tardar ~20s. Revertido: bump SOLO en wake.
        this._lastGenerationReason = reason;
        this._onrefresh = false;

        if (this._sendchanges.length > 0){
            // Reenviar es idempotente en IDENTIDAD (_uuid) pero NO en VERSION:
            // un write en vuelo es un snapshot capturado ANTES del sleep. Si
            // durante la ventana en vuelo se hizo un commit mas reciente del
            // mismo objeto (p.ej. reabrir un ticket pagado -> status AC), ese
            // cambio nuevo ya esta encolado en _syncchanges/_waitchanges.
            // Reencolar el rescatado obsoleto re-marcaria el estado viejo
            // (ticket pagado) y dispararia un falso "pagado en otro
            // dispositivo" que cierra el editor solo. Como cada cambio lleva
            // el snapshot completo de la fila y CommitChanges ya deduplica por
            // clave quedandose con el ultimo, descartar el rescatado es seguro:
            // el encolado mas nuevo produce el estado final correcto.
            let _keep = [];
            let _stale = 0;
            for(let _rescued of this._sendchanges){
                let _entry = (_rescued && _rescued.entrynfo) ? _rescued.entrynfo : {};
                // NUNCA descartar un do_insert: es la UNICA oportunidad de crear
                // la fila. Si el dedup lo tira, todo lo que la referencia (sus
                // TICKETOPTION, el TICKETCHANGE de la cadena fiscal, el TICKETBAI)
                // queda HUERFANO -> @commit_error_relation_not_found al commitear.
                // El re-envio es idempotente por _uuid en el server (do_insert
                // deduplica la fila), asi que mantenerlo siempre es seguro. El
                // dedup solo tiene sentido para do_update/do_delete (evitar
                // re-marcar estado viejo, p.ej. un "pagado" stale tras reabrir).
                let _isInsert = (_entry['_actn'] == 'do_insert');
                if (!_isInsert &&
                    ((this._IndexOfChange(_rescued, this._syncchanges) != -1) ||
                     (this._IndexOfChange(_rescued, this._waitchanges) != -1))){
                    _stale++;
                    continue;   // solo dedup do_update/do_delete
                }
                _keep.push(_rescued);
            }

            console.info("[SESSION] Rescatando " + _keep.length + " writes en vuelo del XHR cancelado" + (_stale > 0 ? " (" + _stale + " obsoletos descartados)" : ""));
            this._syncchanges = _keep.concat(this._syncchanges);
            this._sendchanges = [];
        }

        // Emitir la senal de "refresh terminado" tambien al cancelar: quien
        // espera en OnRefreshCompleted (_CancelOngoing / _ForceRefresh) se
        // suscribio mientras _onrefresh==true y solo se libera con un
        // _refreshCompleted. Sin esto, tras cancelar (watchdog / wake iOS) el
        // waiter queda colgado hasta un refresh posterior que emita — y si el
        // siguiente _DoRefresh hace early-return (EXPIRED / _session==null) sin
        // emitir, el await no vuelve nunca (cuelga SetPlace/CommitChanges).
        this._refreshCompleted.next();

        return true;
    }
    private async _resumeRefresh(){
        let _expired = (this._rfrstatus == 'EXPIRED');
        if (!_expired){
            if (this._rfrstatus == 'RUNNING'){
                console.warn("[INITIALIZE] Already running!");
                return;
            }
    
            this._rfrstatus = 'RUNNING';

            // Datáfono mode: cache-first. Do NOT block the boot waiting
            // for the first _DoRefresh to finish — IndexedDB already has
            // the last-known state and the UI can render from that while
            // the server delta trickles in.
            if (this.platform && this.platform._isDatafono){
                this._DoRefresh(true)
                    .catch((error) => {
                        console.error("[INITIALIZE] Initial refresh failed in datáfono mode", error);
                    })
                    .then(() => {
                        if (this._rfrstatus == 'RUNNING'){
                            this._repeatRefresh();
                        }
                    });
                return;
            }

            await this._DoRefresh(true);
            if (this._rfrstatus != 'RUNNING'){
                return;     // someone stopped the refresh
            }
    
            this._repeatRefresh();    
        }
    }

    private async _pauseRefresh(){
        let _expired = (this._rfrstatus == 'EXPIRED');
        if (!_expired){
            this._rfrstatus = 'STOPPED';
            await this._CancelOngoing();    
        }
    }

    private async _finalizeRefresh(){
        let _expired = (this._rfrstatus == 'EXPIRED');
        
        await this._pauseRefresh();
        if (!_expired && this._started){
            await this._DoRefresh();        
        }
    }
    
    /**********************************/
    /* REFRESH OPERATION              */
    /**********************************/

    private _onExpired = new Subject<any>();
    public OnExpired = this._onExpired.asObservable();

    private _expired_notified = false;
    async _OnSessionExpired(){
        if (this._expired_notified){
            return;
        }

        if (this._rfrstatus != 'EXPIRED'){
            this._expired_notified = true;

            await this._pauseRefresh();
            this._rfrstatus = 'EXPIRED';

            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@user_session_expired_title'),
                message: this.lang.tr('@user_session_expired_message'),
                backdropDismiss: true,
                buttons: [{
                    text: this.lang.tr('@ok'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);
        
                        return false;
                    }
                }]
            });
            
            alert.onDidDismiss().then(
            data => {
                this._expired_notified = false;    
                this._onExpired.next();
            });

            await alert.present();
        }
    }

    /********************************/
    /* SERVER REFRESH               */
    /********************************/

    private _serverreference = null;    // server elapsed time reference
    
    private set serverreference(value){
        this._serverreference = value;
    }

    private get serverreference(){
        let _ms = performance.now() - this._serverreference;
        return Math.round(_ms / 1000);
    }

    // Referencia de SERVIDOR (segundos) que llevaria un cambio enviado AHORA: la misma
    // formula que _resolve_syncchanges pone en change.reference, y con la que el
    // servidor sella `updated` (synchronize.php). null si aun no hay referencia del
    // servidor. Permite comparar una fila de otro dispositivo con nuestro envio en el
    // mismo reloj (el del servidor), como hace check_replace.
    get CurrentServerReference(): number {
        if (!this._reference || (this._serverreference === null)){
            return null;
        }
        return this._reference + this.serverreference;
    }

    private _uuids_done = new Map();           // relation between the uuids and the objids

    private _resolvedobjid(_uuid){
        return this._uuids_done.get(_uuid) || null;
    }

    private _onChange = new Subject<any>();     // change recovered from server
    public OnChange = this._onChange.asObservable();

    private _onReload = new Subject<any>();     // change recovered from synchanges
    public OnReload = this._onReload.asObservable();

    public DoRefresh(change){
        this._onChange.next(change);

        // change is applied: remove from resolved map
        if (('_uuid' in change) && (this._uuids_done.has(change['_uuid']))){
            this._uuids_done.delete(change['_uuid']);
        }
    }

    private _syncchanges: Array <SyncChange> = [];
    private _waitchanges: Array <SyncChange> = [];

    private _resolve_syncchanges(){
        let _serverreference = this._reference + this.serverreference;  // adjust local interval to server time

        for(let _change of this._syncchanges){
            _change['reference'] = _serverreference;

            if (_change.entrynfo['_actn'] == "do_insert"){
                let _objid = this._resolvedobjid(_change.entrynfo['_uuid']);
                if (_objid){
                    _change.entrynfo['objid'] = _objid;
                    _change.entrynfo['_actn'] = "do_update";
                }
            }
        }
    }

    private _resolve_waitchange(_change){
        if ('_uuid' in _change.relation){
            let _objid = this._resolvedobjid(_change.relation['_uuid']);
            if (_objid){
                _change.relation['objid'] = _objid;
            }
        }

        for(let _requires in _change.requires){
            this._resolve_waitchange(_change.requires[_requires]);
        }
    }

    private _resolve_waitchanges(updates){
        // keep the resolved items in the _uuid list
        for (let _update of updates){
            if ('_uuid' in _update){
                this._uuids_done.set(_update['_uuid'], _update['objid']);
            }
        }

        // resolve any waiting change to the final value
        for(let _change of this._waitchanges){
            if (_change.entrynfo['_actn'] == "do_insert"){
                let _objid = this._resolvedobjid(_change.entrynfo['_uuid']);
                if (_objid){
                    _change.entrynfo['objid'] = _objid;
                    _change.entrynfo['_actn'] = "do_update";
                }
            }

            this._resolve_waitchange(_change);

            // skip update if change is still pending
            for(let _idx = updates.length - 1; _idx >= 0; _idx--){
                let _update = updates[_idx];
                if (_change.entrynfo['table'] == _update['table']){
                    let _skip = false;

                    if (('_uuid' in _change.entrynfo) && (_change.entrynfo['_uuid'] == _update['_uuid'])){
                        _skip = true;   // found change by _uuid
                    }

                    if (('objid' in _change.entrynfo) && (_change.entrynfo['objid'] == _update['objid'])){
                        _skip = true;   // found change by objid
                    }

                    if (_skip){
                        updates.splice(_idx, 1);
                    }
                }
            }
        }

        // set the new syncchanges set
        this._syncchanges = this._waitchanges;        
        this._waitchanges = [];
    }

    private _append_waitchanges(){
        if (this._waitchanges.length > 0){
            this.CommitChanges(this._waitchanges, false);
        }

        this._waitchanges = [];
    }

    get Changes(){
        return this._syncchanges;
    }

    /** Ticket/objeto ya encolado en sync o wait (dedup por _uuid u objid).
     *  includeInFlight: cuenta tambien lo que esta en el POST en curso (_sendchanges).
     *  Solo para el rescate de cobros: _hasServerSyncInFlight (Ticket._CanUpdate) NO
     *  debe usarlo, porque en el camino que descarta el refresh pero confirma los
     *  writes propios (_pending.Concat) _sendchanges aun esta lleno y bloquearia el
     *  eco que trae el objid del ticket nuevo. */
    HasQueuedChange(table: string, uuid?: string, objid?: any, includeInFlight: boolean = false): boolean {
        const _match = (c: SyncChange) => {
            if (c.entrynfo['table'] !== table){
                return false;
            }
            if (uuid && ('_uuid' in c.entrynfo) && c.entrynfo['_uuid'] === uuid){
                return true;
            }
            if (objid != null && ('objid' in c.entrynfo) && c.entrynfo['objid'] == objid){
                return true;
            }
            return false;
        };

        return this._syncchanges.some(_match) || this._waitchanges.some(_match)
            || (includeInFlight && this._sendchanges.some(_match));
    }

    /** Update del servidor para este objeto recibido pero aun sin aplicar (cola de
     *  Pending). Ver Pending.Has. */
    HasPendingUpdate(table: string, uuid?: string, objid?: any): boolean {
        return this.Pending.Has(table, objid, uuid);
    }

    // ¿La ultima peticion de refresh obtuvo respuesta HTTP del servidor? false con
    // status 0 (sin red) o 504. ConnectedServer._isconnected solo cambia al crear o
    // borrar la conexion, no refleja una caida de red; esto si.
    private _serverReachable = true;
    get ServerReachable(): boolean {
        return this._serverReachable;
    }

    private _refreshCompleted = new Subject<any> ();
    public OnRefreshCompleted = this._refreshCompleted.asObservable();
    // Lote rechazado al encolar (SortChanges) y retirado de la cola: sus objetos deben
    // re-marcarse como pendientes (dataService) o nunca se reenviarian.
    private _changesRejected = new Subject<Array <SyncChange>> ();
    public OnChangesRejected = this._changesRejected.asObservable();
    private _commitCompleted = new Subject <any> ();
    public OnCommitCompleted = this._commitCompleted.asObservable();

    /** Espera a que NO queden writes pendientes (ni en cola ni en vuelo). Lo usa
     *  el cierre de caja para no computar/imprimir el AUDIT antes de que los
     *  tickets recien pagados esten en el server (auditinfo.php calcularia un
     *  cierre INCOMPLETO -papel con menos tickets- hasta recargar).
     *
     *  Solo LEE el estado; el ciclo de refresh en curso es quien envia los
     *  pendientes. Devuelve true si todo se vacio dentro del timeout; false si se
     *  agoto (p.ej. un write atascado) para que el caller AVISE en vez de
     *  imprimir/cerrar en silencio con datos incompletos.
     *
     *  Default 60s: alineado con el _reqtimeout de un commit con writes en
     *  _DoRefresh (60000). Un write en vuelo (_sendchanges) puede tardar hasta ese
     *  limite en resolverse; con menos, FlushPending podria devolver false mientras
     *  el commit sigue vivo -> cierre incompleto igual (Copilot). En red normal se
     *  vacia en <1s (synchronize.php responde inmediato a los writes), asi que en
     *  el caso comun no hay espera perceptible. */
    public async FlushPending(timeoutMs: number = 60000): Promise<boolean> {
        const _start = Date.now();
        const _count = () => this._syncchanges.length + this._waitchanges.length + this._sendchanges.length;
        while (_count() > 0){
            if ((Date.now() - _start) >= timeoutMs){
                console.warn("[SYNC] FlushPending timeout — quedan " + _count() + " write(s) sin sincronizar");
                return false;
            }
            await new Promise(_r => setTimeout(_r, 250));
        }
        return true;
    }

    private async _CacheLoad(){
        this._stage_target = null;     // stablish the cache target for this load / save operation

        if (this._timestamp == 0){
            let _data = null;

            let _subscription = this.Pending.OnRefreshStage.subscribe(
            stage => {
                if (this.LoadTarget != 'SESSION'){
                    if (stage > 6){     // wait for main objects to be loaded
                        _subscription.unsubscribe();
    
                        // add the stored changes waiting to be synchronized
                        for (let _change of this._syncchanges){
                            this._onReload.next(_change);
                        }
                    }    
                }
            });
    
            switch(this.LoadTarget){
                case 'SESSION':
                    _data = await this.store.LoadSessionData(this._session.device, this._session.session);
                    break;

                case 'PLACE':
                    _data = await this.store.LoadPlaceData(this._session.place.objid);
                    break;
            }

            if (_data){
                console.info("[SYNCHRONIZE] Loaded from (" + this.LoadTarget + ") cache [" + _data['updates'].length + "] entries");
                this._timestamp = _data['timestamp'];
                console.info("[SYNCHRONIZE] updates (" + this.LoadTarget + ") will be resumed since: " + this._timestamp);

                if (this._pending){     // is destroyed??
                    let _updates = _data['updates'];

                    // Datafono salta cash-control al boot — se hidrata on-demand en ViewTill.
                    if (this.IsDatafono && this.LoadTarget == 'PLACE'){
                        const _skipped = [];
                        const _kept = [];
                        for (const _u of _updates){
                            if (syncService.DATAFONO_LAZY_TABLES.has(_u['table'])){
                                _skipped.push(_u);
                            }
                            else {
                                _kept.push(_u);
                            }
                        }
                        this._datafonoSkippedUpdates = _skipped;
                        _updates = _kept;
                        console.info("[BOOT] Datafono: deferred " + _skipped.length + " entries (cash-control history) — will hydrate on viewtill click");
                    }

                    this._pending.Concat(_updates, false);
                }

                this._refreshCompleted.next();
            }
        }

        let _getparams = {
            access: this._access,           // access mode
            timestamp: this._timestamp,     // last queried timestamp
            target: this.LoadTarget         // the loaded target
        };

        if (this._session.place && this._session.place.objid){
            _getparams['place'] = this._session.place.objid;
        }

        if (this._session.qrcode && this._session.qrcode.objid){
            _getparams['table'] = this._session.qrcode.objid;
        }

        return _getparams;
    }

    private async _CacheSave(data){
        if (!data){
            return;     // no data provided 
        }

        switch(data['target'] || this.LoadTarget){
            case 'SESSION':
                return await this.store.SaveSessionData(this._session.device, this._session.session, data['updates'], data['timestamp']);
            case 'PLACE':
                return await this.store.SavePlaceData(this._session.place.objid, data['updates'], data['timestamp']);
        }
    }

    private _lastchanges = null;

    private _WriteSyncChanges(){
        if ((this._lastchanges === null) || (this._lastchanges != this._syncchanges.length)){
            this._resolve_syncchanges();    // add server reference value to changes 

            if (this._syncchanges.length > 0){
                console.info("[STORAGE]: Updating the changeset with " + this._syncchanges.length + " changes.");
            }
        }

        this._lastchanges = this._syncchanges.length;   // reduce number of logs (only on size changed)
    }

    private _toreset = false;
    private _sendchanges = [];
    private _retrycount = 0;
    private _offlineSyncWarned = false;
    private _lastOfflineSyncLogAt = 0;

    private _prepare_syncronize(){
        this._WriteSyncChanges();

        this._sendchanges = this._syncchanges;
        this._syncchanges = [];
    }

    private async _onsuccess_syncronize(){
        this._sendchanges = [];               
        this.store.ClearChanges(await this.wsstore.GetStorage());
        this._retrycount = 0;
        this._offlineSyncWarned = false;
    }

    private async _onfailure_synchronize(retry){
        const _maxretries = 10;

        if (retry){
            this._retrycount++;
        }

        if (this._retrycount > _maxretries){
            console.error("[SERVICE] No more retries - DATA WILL BE LOST");
            this.toast.ShowAlert("danger", this.lang.tr("@lost_changes_error"), 5000);
            this._retrycount = 0;
        }
        else {  // prepare changes for next retry operation
            if (retry){
                console.warn("[SERVICE] Retrying synchronization (" + this._retrycount + " of " + _maxretries+ ")");
            }
            else {
                const _now = Date.now();
                if (!this._offlineSyncWarned || (_now - this._lastOfflineSyncLogAt >= 60000)){
                    console.warn("[SERVICE] Retrying synchronization (server connection unavailable)");
                    this._offlineSyncWarned = true;
                    this._lastOfflineSyncLogAt = _now;
                }
            }
            // Solo restaurar si habia algo en vuelo. Si _DoRefresh fallo ANTES de
            // _prepare_syncronize (p.ej. _CacheLoad lanza), _sendchanges esta vacio
            // y pisar _syncchanges perderia los cambios ya encolados hasta recargar
            // — incluidos los promovidos de _waitchanges en un commit forzado.
            if (this._sendchanges.length > 0){
                this._syncchanges = this._sendchanges;
            }
        }

        this._sendchanges = [];
    }

    private _onServerTime = new Subject<boolean>();
    public OnServerTime = this._onServerTime.asObservable();

    // Margin: 180s (3 min). A real misconfigured device clock is off by
    // minutes/hours, not by ~30s. 60s still triggered false positives on VMs
    // (hypervisor clock drift, e.g. VMware) and on single slow HTTP responses
    // (busy backend, lossy WiFi in restaurants).
    //
    // NOT cosmetic: a bad reading BLOCKS charging (place.ts checks
    // view.TimeCheck alongside the TicketBAI/Verifactu validations — the
    // fiscal signature needs a reliable clock). Widening to 180s stops false
    // blocks on drifting VM clocks while still catching a genuinely wrong
    // clock (off by many minutes/hours). Kept well under the AEAT/Batuz
    // tolerance so a 3-min skew never invalidates the record.
    //
    // Hysteresis: require N consecutive bad reads before showing the warning,
    // a single good read clears it. With MainRefresh=500ms that's ~1.5s of
    // sustained drift before the banner appears, fast recovery after.
    private static readonly SERVER_TIME_MARGIN_S = 180;
    private static readonly SERVER_TIME_BAD_READS_THRESHOLD = 3;

    private _bad_servertime_count = 0;

    private _check_servertime(mysqldate){
        let _str = mysqldate ? mysqldate.replace(' ', 'T') + 'Z' : null;

        let _srv = new Date(_str);
        if (isNaN(_srv.getTime())){
            return;    // error in input date
        }

        const _localDate = Math.floor(new Date().getTime() / 1000);
        const _servrDate = Math.floor(_srv.getTime() / 1000);
        const _diff = Math.abs(_localDate - _servrDate);

        if (_diff <= syncService.SERVER_TIME_MARGIN_S){
            this._bad_servertime_count = 0;
            this._onServerTime.next(true);
        }
        else {
            this._bad_servertime_count++;
            if (this._bad_servertime_count === syncService.SERVER_TIME_BAD_READS_THRESHOLD){
                // Log only on the transition to "bad" state. While drift
                // persists this method runs every MainRefresh (~500ms), so
                // re-warning each tick would flood the console.
                console.warn("[LOCAL DATE] diff=" + _diff + "s, local=" + new Date(_localDate * 1000).toISOString() + ", server=" + new Date(_servrDate * 1000).toISOString());
            }
            if (this._bad_servertime_count >= syncService.SERVER_TIME_BAD_READS_THRESHOLD){
                this._onServerTime.next(false);
            }
        }
    }

    // Generation token para descartar handlers de XHR zombies (iOS sleep).
    // Cada _DoRefresh incrementa este contador y su valor local se cierra en
    // los handlers .then/.error. Si al llegar el response la generation ya
    // no coincide (porque un wake handler incremento el contador para
    // "cancelar" el XHR viejo), los handlers hacen no-op y no corrompen el
    // estado del nuevo refresh que este en curso.
    private _refreshGeneration = 0;
    // Ultimo motivo por el que subio _refreshGeneration. Para logs honestos:
    // un ".then descartado" puede deberse a un commit, al watchdog, a un wake
    // o simplemente a que arranco un refresh nuevo — NO siempre "iOS sleep".
    private _lastGenerationReason = 'inicial';
    // Generacion de supersedes INSEGUROS: sube en _cancelInflightRefresh para
    // TODO reason != 'commit' (wake iOS/bfcache/network, watchdog, refresh-error).
    // El guard de generacion nacio para el ZOMBIE (un XHR reanimado tras suspender
    // o cancelado a medias que clobberea los writes rescatados: facturas-sin-
    // lineas). Pero _refreshGeneration tambien sube en cada 'commit', y ahi el
    // supersede NO es un zombie: superamos un poll idle SANO con nuestro propio
    // write, asi que su respuesta READ es valida y hay que APLICARLA (como en
    // 2.0.2.8) — si se descartaba, el push del receptor se perdia y el cambio
    // tardaba ~20s. Con esta 2a generacion distinguimos supersede INSEGURO
    // (descartar) de supersede por 'commit' (aplicar), restaurando el instantaneo
    // sin reabrir el zombie.
    private _wakeGeneration = 0;

    private _DoRefresh(_reset = false, _synchronous = false){
        let _connected = true;

        return new Promise(async (resolve) => {
            if (!await this.Connected.UpdateConnection(this._timestamp) && (this._timestamp != 0)){
                _connected = false;     // not connected (only full refresh)
            }

            if (_reset){    // wait until the next request is sent
                this._toreset = true;
            }

            if (this._session == null){
                resolve(false);  // session has been released
                return;
            }

            // Sesion expirada: mismo motivo que _ForceRefresh — cada retry con
            // errorcode==2 acerca al descarte por _retrycount>10. Bloqueamos
            // hasta que login reanude via _initializeRefresh (reset a STOPPED).
            // Guard tambien contra _expired_notified: ver comentario en
            // _ForceRefresh — ventana async entre _expired_notified=true y
            // _rfrstatus='EXPIRED' donde _pauseRefresh aun no ha terminado.
            if (this._rfrstatus == 'EXPIRED' || this._expired_notified){
                resolve(false);
                return;
            }

            if (!this._started || this._onrefresh){
                resolve(!this._started);   // already refreshing (true) / session released (false)
            }
            else {
                this._onrefresh = _connected;
                this._refreshStartedAt = Date.now();    // para el watchdog de _repeatRefresh
                // Capturar la generation de este refresh. Los handlers async
                // van a comparar contra `this._refreshGeneration` (el valor
                // vigente en el momento de llegar el response). Si un wake
                // handler ha incrementado el contador, `_myGeneration` no
                // coincidira y los handlers se descartan silenciosamente.
                this._refreshGeneration++;
                this._lastGenerationReason = 'refresh nuevo en curso';
                const _myGeneration = this._refreshGeneration;
                const _myWakeGen = this._wakeGeneration;   // para distinguir supersede por WAKE vs commit
                try {
                    if (this._toreset){
                        this._toreset = false;
                        this.Pending.Reset();
                        this._timestamp = 0;
                    }

                    let _getparams = await this._CacheLoad();    // recovers localy stored data

                    // Guard post-await: _CacheLoad es un punto de suspension.
                    // Si iOS congelo la app AQUI y _retryAfterWake hizo gen++
                    // (+ rescate de _sendchanges) mientras tanto, este refresh
                    // viejo NO debe continuar: su _prepare_syncronize robaria
                    // los writes recien rescatados hacia un _sendchanges cuyo
                    // request va a ser descartado por el guard de generation —
                    // la misma perdida que el rescate evita, por otra puerta.
                    if (_myGeneration !== this._refreshGeneration){
                        console.info("[SYNCHRONIZE] refresh abortado post-CacheLoad — generation " + _myGeneration + " != " + this._refreshGeneration);
                        resolve(false);
                        return;
                    }

                    if (!_connected){
                        if (this._waitchanges.length > 0){
                            // if any waitchange is stored when connection is lost, recover it here
                            for(let _waitchange of this._waitchanges){
                                this._syncchanges.push(_waitchange);
                            }

                            this._waitchanges = [];
                        }

                        resolve(true);
                    }
                    else {  // we are connected. Synchronize with the server
                        this._prepare_syncronize();

                        let _sentchanges = this._sendchanges.length;
                        // Clave de los objetos que enviamos en ESTE write. Si la
                        // respuesta se descarta por el guard de generacion, la usamos
                        // para confirmar SOLO nuestros writes (ver el ".then descartado"
                        // mas abajo). Clave por `tabla|uuid|<_uuid>` (inserts, sin objid)
                        // o fallback `tabla|objid|<objid>` (updates/deletes, con objid) —
                        // entrynfo trae _uuid solo cuando no hay objid, asi que sin el
                        // fallback los do_update/do_delete propios no se confirmarian.
                        // Se incluye la tabla para no casar por colision de clave entre
                        // tablas. El place ya lo acota el server (refresh por place).
                        let _sentKeys = new Set(this._sendchanges
                            .map(_c => {
                                let _e = _c && _c.entrynfo;
                                if (!_e || !_e['table']) return null;
                                if (_e['_uuid']) return _e['table'] + '|uuid|' + _e['_uuid'];
                                if (_e['objid'] != null) return _e['table'] + '|objid|' + _e['objid'];
                                return null;
                            })
                            .filter(_k => !!_k));
                        // synchronize.php devuelve INMEDIATAMENTE cuando se envian writes
                        // (refresh='COMMITED', sin el long-poll de 20s — synchronize.php:750/768).
                        // Asi que un commit que se cuelga es una conexion muerta: lo acotamos
                        // para que el CommitChanges que la UI espera se libere y re-encole la
                        // comanda offline, en vez de colgar el spinner "enviar comanda".
                        //
                        // OJO (2026-07-10): 15s era DEMASIADO corto. Un changeset grande
                        // (dividir+añadir+reabrir) que el server SI esta procesando bien tarda
                        // >15s -> este timeout abortaba un commit exitoso -> _onfailure_synchronize
                        // re-encolaba y reenviaba a los ~15s -> BOLA DE NIEVE (log del server:
                        // la misma R00000017 reenviada 5 veces en 52s; y el reintento re-corre
                        // LogTicketChange -> R duplicada con uuid distinto que el server no puede
                        // deduplicar). Subimos a 60s: un commit vivo-pero-lento termina antes de
                        // abortar; solo una conexion realmente muerta llega al limite.
                        // (Trade-off: offline-real tarda hasta 60s en soltar el spinner y
                        //  re-encolar — tunear si se hace notar.)
                        let _reqtimeout = (_sentchanges > 0) ? 60000 : null;
                        this.DoRequest(this._doRefreshUrl, _getparams, this._sendchanges, false, null, null, null, _reqtimeout)
                        .then(async data => {
                            this._serverReachable = true;
                            // Guard: si un wake handler incremento la
                            // generation, este .then pertenece a un XHR
                            // zombie que iOS reanimo tras suspender la app.
                            // NO tocar estado — el refresh vigente esta en
                            // curso con otro _myGeneration.
                            //
                            // resolve(false) obligatorio: sin el, el Promise
                            // del _DoRefresh viejo queda colgado para siempre
                            // y bloquea al caller. _repeatRefresh usa .then
                            // (el loop de refresh se pararia), _resumeRefresh
                            // / _ForceRefresh usan await (colgados). El nuevo
                            // _DoRefresh del wake handler ya asumio la
                            // responsabilidad, este viejo solo tiene que
                            // desatascarse limpio.
                            if (_myGeneration !== this._refreshGeneration){
                                // REGRESIÓN del refresco instantáneo (era así en 2.0.2.8, que
                                // NO tenía guard): si el poll se superó por un COMMIT normal
                                // (no por un WAKE de iOS) y es un poll de SOLO LECTURA
                                // (_sentchanges==0), sus updates son válidos y hay que
                                // APLICARLOS — descartarlos perdía el push del receptor y el
                                // cambio tardaba ~20s. El guard anti-zombie se reserva para el
                                // supersede por WAKE (iOS sleep/bfcache/network), que es el
                                // único caso de clobber (facturas-sin-lineas).
                                let _wakeSuperseded = (_myWakeGen !== this._wakeGeneration);
                                if (!_wakeSuperseded && _sentchanges === 0 && data && data['errorcode'] == 0
                                        && Array.isArray(data['updates']) && data['updates'].length > 0 && this._pending){
                                    // Poll de SOLO LECTURA superado por un commit (no por un
                                    // wake): aplicamos sus updates a la vista para no perder el
                                    // push del receptor (como en 2.0.2.8). IMPORTANTE: NO tocamos
                                    // _timestamp/_reference/_CacheSave — este poll fue superado y
                                    // regresar el timestamp por debajo del que dejó el poll que lo
                                    // superó provocaría re-syncs. Concat es idempotente por objid,
                                    // así que el siguiente poll re-trae estos updates sin daño.
                                    console.info("[SYNCHRONIZE] supersede por commit (no wake): aplicando " + data['updates'].length + " updates a la vista (sin tocar timestamp)");
                                    this._pending.Concat(data['updates'], false);
                                    resolve(false);
                                    return;
                                }
                                console.info("[SYNCHRONIZE] .then descartado — generation " + _myGeneration + " != " + this._refreshGeneration + " (superado por: " + this._lastGenerationReason + ", wake=" + _wakeSuperseded + ")");
                                // OPCION A (raiz de facturas-sin-lineas + tormenta de
                                // reenvios): descartamos el REFRESH (evita clobber con
                                // datos de otros dispositivos, que es lo que el guard
                                // protege), PERO confirmamos NUESTROS writes. Sin esto,
                                // el server ya persistio las lineas pero el cliente tira
                                // el ACK -> el objeto no recibe objid, _inCommit queda
                                // colgado y se reenvia en bucle (deduped en server), y si
                                // ademas el write se perdio, la cabecera fiscal queda sin
                                // lineas. Filtramos los updates a los _uuid que enviamos:
                                // resuelve solo nuestros objetos, nada ajeno.
                                if (_sentchanges > 0 && data && data['errorcode'] == 0
                                        && Array.isArray(data['updates']) && this._pending){
                                    let _mine = data['updates'].filter(_u => {
                                        if (!_u || !_u['table']) return false;
                                        if (_u['_uuid'] && _sentKeys.has(_u['table'] + '|uuid|' + _u['_uuid'])) return true;
                                        if (_u['objid'] != null && _sentKeys.has(_u['table'] + '|objid|' + _u['objid'])) return true;
                                        return false;
                                    });
                                    if (_mine.length > 0){
                                        console.info("[SYNCHRONIZE] .then descartado: confirmando " + _mine.length + " write(s) propio(s) (evita reenvio en bucle)");
                                        this._pending.Concat(_mine, false);
                                    }
                                }
                                resolve(false);
                                return;
                            }

                            // Response recibida con generation vigente: el
                            // pipe funciona — resetear strikes del watchdog.
                            this._watchdogStrikes = 0;

                            if (data['systemdate']){    // check if local time is valid
                                this._check_servertime(data['systemdate']);
                            }

                            if (data['updated']){       // server has been updated
                                this._onsuccess_syncronize();
                            }

                            let success = (data['errorcode'] == 0);
                            if (success){
                                if (_sentchanges > 0){
                                    console.info("[SYNCHRONIZE] Writing [" + _sentchanges + "] entries to server");

                                    let _subscription = this.OnRefreshCompleted.subscribe(
                                    data => {
                                        _subscription.unsubscribe();
                                        this._commitCompleted.next();
                                    });
                                }
                                else {
                                    if (data['updates'].length > 0){
                                        console.info("[SYNCHRONIZE] Updating [" + data['updates'].length + "] entries from server");
                                    }
                                }

                                this._resolve_waitchanges(data['updates']);

                                if (data['timestamp'] != 0){
                                    await this._CacheSave(data);     // update localy stored data
                                }

                                this._timestamp = data['timestamp'];
                                this._reference = data['reference'];

                                if (this._pending){     // is destroyed??
                                    this._pending.Concat(data['updates'], _synchronous);
                                }

                                if (data['expired']){
                                    console.warn("[SERVICE] Client connection information was lost or expired: Reconnecting");
                                    this._connected = null;         // generate the connection information
                                }
                            }
                            else {
                                if ((_sentchanges > 0) && (data['updated'] == false)){
                                    this._onfailure_synchronize(true);
                                }

                                if (data['errorcode'] == 2) {
                                    this._OnSessionExpired();
                                }
                                else {
                                    console.error("[SERVICE] Error [" +  data['errorcode'] + "] in '" + this._doRefreshUrl + "'");
                                    this.toast.ShowAlert('danger', this.lang.tr('@service_request_error_' + data['errorcode']));
                                }
                            }

                            this.serverreference = performance.now();

                            this._onrefresh = false;
                            this._refreshCompleted.next();
                            this._append_waitchanges();

                            if (success){
                                resolve(true);
                            }
                            else {
                                console.warn("[SERVICE] Delaying next refresh 10s — errorcode=" + data['errorcode'] + " updated=" + data['updated'] + " expired=" + data['expired']);
                                setTimeout(() => {
                                    resolve(false);
                                }, 10000);  // delay next refresh (10 secs) - this is a server error
                            }
                        }, error => {
                            // Guard: ver comentario en el .then. Aplica tambien
                            // al error handler porque _onfailure_synchronize
                            // manipula _syncchanges/_sendchanges y corromperia
                            // el refresh vigente.
                            if (_myGeneration !== this._refreshGeneration){
                                console.info("[SYNCHRONIZE] error descartado — generation " + _myGeneration + " != " + this._refreshGeneration + " (superado por: " + this._lastGenerationReason + ")");
                                resolve(false);
                                return;
                            }

                            this._serverReachable = (error.status != 0) && (error.status != 504);
                            this._onfailure_synchronize(this._serverReachable);

                            if (error.status != 0){
                                console.error("[REQUEST] Error [" + error.status + "] in http request '" + this._doRefreshUrl + "'");
                            }

                            this._onrefresh = false;
                            this._refreshCompleted.next();
                            this._append_waitchanges();

                            setTimeout(() => {
                                resolve(false);
                            }, 2000);  // delay next refresh (2 secs) - this is a communication error
                        });
                    }
                }
                catch(error){
                    // Mismo guard que en .then/error. Excepciones de _CacheLoad
                    // o _prepare_syncronize son async y podrian llegar despues
                    // de un wake handler que incremento la generation.
                    if (_myGeneration !== this._refreshGeneration){
                        console.info("[SYNCHRONIZE] catch descartado — generation " + _myGeneration + " != " + this._refreshGeneration);
                        resolve(false);
                        return;
                    }

                    this._onfailure_synchronize(false);

                    console.error(error);

                    this._onrefresh = false;
                    this._refreshCompleted.next();
                    this._append_waitchanges();

                    setTimeout(() => {
                        resolve(false);
                    }, 2000);   // delay next refresh (2 sec) - this is a client error
                }
            }
        });
    }

    private _indexOfRelation(_require, _changes){
        for(let _idx = 0; _idx < _changes.length; _idx++){
            let _relation = _changes[_idx].relation;
            if ('_uuid' in _relation){
                if ((_relation['table'] == _require['table']) && (_relation['_uuid'] == _require['_uuid'])){
                    return _idx;
                }
            }
        }

        return -1;  // relation not found
    }

    private CheckDepends(a, b){
        let _relation = b.relation;

        for(let _dfield in a.requires){
            // check if a is direct dependecy of b
            let _requires = a.requires[_dfield].relation;
            if ('_uuid' in _requires){  
                if ((_relation['table'] == _requires['table']) && (_relation['_uuid'] == _requires['_uuid'])){
                    return true;    // a is direct dependency of b
                }
            }

            // check if a is indirect dependency of b
            let _indirect = a.requires[_dfield].requires;
            for (let _ifield in _indirect){
                if (this.CheckDepends(_indirect[_ifield], b)){
                    return true;    // a is indirect dependency of b
                }    
            }
        }

        return false;    // a is not a dependency of b
    }

    private SortChanges(_changes){
        let _pvt = 0;
        while(_pvt < _changes.length){
            let _hasDepends = false;
            for(let _idx = _pvt+1; _idx < _changes.length; _idx++){
                _hasDepends = this.CheckDepends(_changes[_pvt], _changes[_idx]);
                if (_hasDepends){
                    let _swap = _changes[_idx];
                    _changes[_idx] = _changes[_pvt];
                    _changes[_pvt] = _swap;

                    break;  // dependecy found (swapped with current)
                }
            }

            if (!_hasDepends){
                _pvt++;     // pivot has no dependencies, move to next
            }
        }

        // check for missing relations
        for(let _idx=0; _idx < _changes.length; _idx++){
            let _change = _changes[_idx];
            for(let _field in _change.requires){
                let _require = _change.requires[_field].relation;
                if (!('objid' in _require) || (!_require.objid)){
                    let _message = null

                    let _pos =  this._indexOfRelation(_require, _changes);
                    if (_pos == -1){
                        _message = "@commit_error_relation_not_found";
                    }

                    if (_pos > _idx){
                        _message = "@commit_error_relation_bad_order";
                    }

                    if (_message){
                        let _error = {
                            missing: _change,
                            require: _require,
                            changes: _changes.slice(0),
                            message: _message
                        };

                        console.error("[COMMIT ERROR]: ", { error: _error, json: JSON.stringify(_error) });
                        throw(_error);    
                    }
                }
            }
        }
    }

    SortUpdates(_updates){
        return this.Pending.SortUpdates(_updates);
    }

    private _IndexOfChange(needle: SyncChange, haystack: Array <SyncChange>){
        for(let _idx = 0; _idx < haystack.length; _idx++) {
            let _change = haystack[_idx];
            if (needle.entrynfo['table'] == _change.entrynfo['table']){
                if ('objid' in needle.entrynfo){
                    if (needle.entrynfo['objid'] == _change.entrynfo['objid']){
                        return _idx;    // same table / objid
                    }
                }

                if ('_uuid' in needle.entrynfo) {
                    if(needle.entrynfo['_uuid'] == _change.entrynfo['_uuid']){
                        return _idx;    // same table / _uuid
                    }
                }
            }
        }

        return -1;   // change not found
    }

    private RollbackChanges(target, changes){
        for(let _change of changes){
            let _idx = target.indexOf(_change);
            if (_idx != -1){
                target.splice(_idx, 1);
            }
        }
    }
  
    private _ForceRefresh(){
        return new Promise(async (resolve) => {
            // Sesion muerta: no lanzar mas requests. Cada _DoRefresh con sesion
            // expirada devuelve errorcode==2, incrementa _retrycount, y en el
            // limite (11) descarta cambios pendientes ("DATA WILL BE LOST").
            // El dialogo modal ya esta bloqueando la UI, pero timers/CommitChanges
            // en cola pueden colar un _ForceRefresh antes del reset por login.
            //
            // Guard tambien contra _expired_notified: _OnSessionExpired hace
            //   _expired_notified = true; await _pauseRefresh(); _rfrstatus='EXPIRED';
            // Entre la primera y la tercera linea hay una ventana async (CancelOngoing
            // dentro de _pauseRefresh) donde _rfrstatus aun no es EXPIRED pero la
            // sesion ya se ha detectado como muerta. Sin este segundo check un timer
            // podria colar un request en esa ventana.
            if (this._rfrstatus == 'EXPIRED' || this._expired_notified){
                resolve(false);
                return;
            }

            if (this._onrefresh){
                let _subscription = this.OnRefreshCompleted.subscribe(
                async data => {
                    _subscription.unsubscribe();
                    await this._DoRefresh(false, true);
                    resolve(true)
                })
            }
            else {
                await this._DoRefresh(false, true);
                resolve(true);
            }
        });
    }

    private _CancelOngoing(){
        return new Promise(async (resolve) => {
            if (this._onrefresh){
                let _wait = performance.now();
                
                let _subscription = this.OnRefreshCompleted.subscribe(
                async data => {
                    _subscription.unsubscribe();
                    resolve(true);

                    console.info("[COMMIT] Ongoing refresh returned in " + (performance.now() - _wait).toFixed(2) + " ms..");
                })

                // Descartar el refresh en vuelo por GENERATION, no solo abortar el
                // XHR a nivel de red (CancelRefresh pierde la carrera si el XHR ya
                // volvio con datos). Sin esto, al commitear un cambio local (p.ej.
                // reabrir un ticket) mientras hay un long-poll en vuelo, ese refresh
                // vuelve con el estado del server ANTERIOR al cambio y lo PISA
                // ("falso reabierto / se vuelve a pagar"). _cancelInflightRefresh
                // sube _refreshGeneration -> cuando el XHR viejo entra en su .then,
                // choca con el guard de generation y se descarta ANTES de aplicar
                // los datos. Ademas emite _refreshCompleted, que libera el subscribe
                // de arriba al instante (no esperamos los ~5s del long-poll).
                this._cancelInflightRefresh('commit');
                this.Connected.CancelRefresh();
            }
            else {  // no request is ongoing
                this.Connected.CancelRefresh().then(
                data => {
                    resolve(true);  
                });
            }
        });
    }

    private _logcommits = false;    // enable verbose logging of commits
    // Devuelve false si el lote NO se ha encolado (SortChanges lo rechaza: relación
    // perdida / mal orden). Antes se tragaba el error: rollback + toast y seguía como
    // si nada, y DoCommit devolvía true -> el cobro se imprimía y el ticket quedaba
    // "pagado" solo en memoria, sin reenvío posible (Amaru, 24-oct-2026).
    async CommitChanges(changes: Array <SyncChange>, force: boolean): Promise<boolean> {
        let _target = (this._onrefresh) ? this._waitchanges : this._syncchanges;

        console.info("[COMMIT] commiting " + changes.length + " entries (now = " + force + ")");
        for(let change of changes){
            if (this._logcommits){
                console.info("[COMMIT] on table '" + change.entrynfo['table'] + "' (" + (this._onrefresh ? 'synchanges': 'waitchanges') +  "):", change.entrynfo);
            }

            // replace any previous changes over this object
            let _idx = this._IndexOfChange(change, _target);
            if (_idx != -1){    // update
                if (this._logcommits){
                    console.info("[COMMIT] remove previous '" + _target[_idx].entrynfo['table'] + "':", _target[_idx].entrynfo);
                }
                _target.splice(_idx, 1);
            }
            
            _target.push(change);
        }

        // sort the changes on the target (synchanges or waitchanges) list
        if (changes.length > 0){
            try {
                this.SortChanges(_target);
            }
            catch(e){
                this.RollbackChanges(_target, changes);
                this.toast.ShowAlert('danger', this.lang.tr(e.message));
                console.error("[COMMIT] changeset rejected and rolled back (" + changes.length + " entries): nothing sent", e);
                // Aviso a dataService para re-marcar los objetos del lote: cubre tambien
                // los caminos sin llamante que mire el resultado (_append_waitchanges,
                // promocion de _waitchanges), no solo DoCommit.
                this._changesRejected.next(changes);
                return false;   // el llamante (DoCommit) re-marca sus objetos y falla
            }            
        }        

        /*
            We save the target list into the persistent cache, to be recovered in case of failure
            After synchronization (succesfull or not):
            - The stored data is cleared - if waitchanges are stored, then those changes are cleared form cache
            - waitchanges is copied into synchanges and CommitChanges is being called
            - if changes.length > 0, then the cache will be populated with the next set of changes
        */

        if (changes.length > 0){
            // Persistencia (GetStorage + SetChanges) EN PARALELO, sin bloquear la
            // cancelacion: _CancelOngoing debe correr YA (generation++) para
            // descartar el refresh en vuelo antes de que su .then aplique estado del
            // server ANTERIOR al cambio local (el clobber que _cancelInflightRefresh
            // evita). GetStorage puede tardar (crear/reconectar el WS storage), asi
            // que tambien va en paralelo — no antes del cancel. Es seguro: el rescate
            // de _cancelInflightRefresh REEMPLAZA _syncchanges por un array nuevo, no
            // muta el _target que SetChanges esta comprimiendo. Se espera la
            // persistencia despues (antes de la promocion / re-persistencia del
            // bloque force) para conservar el orden de escrituras.
            let _persist = this.wsstore.GetStorage().then(_ws => this.store.SetChanges(_target, _ws));

            // GUARD arranque: el refresh en vuelo ES la carga (SESSION/PLACE).
            // Cancelarlo sube generation y descarta su .then -> LoadStage clavado y
            // app colgada en "inicializando". Solo cortamos el long-poll del server
            // (CancelRefresh no toca generation). Va aqui y no en _CancelOngoing:
            // su otro caller, _pauseRefresh, si necesita cancelar de verdad.
            let _cancel = performance.now();
            if (this._stageready_notified){
                await this._CancelOngoing();
                console.info("[COMMIT] Cancel ongoing completed in: " + (performance.now() - _cancel).toFixed(2) + " ms");
            }
            else {
                this.Connected.CancelRefresh();
                console.info("[COMMIT] Boot: refresh de carga preservado (long-poll cortado en server)");
            }

            // esperar la persistencia antes de continuar: la promocion y la
            // re-persistencia del bloque force deben operar sobre storage consistente.
            await _persist;

            // Antes: solo con force=true. PERO el mismo cuelgue pasaba con
            // force=false (crear producto/familia, cambiar precio/orden): al
            // commitear con un long-poll en vuelo, los cambios van a _waitchanges;
            // si NO se promueven, el siguiente _DoRefresh sale con _syncchanges
            // vacío -> LECTURA idle que aguanta 20s en synchronize.php y el write
            // real se enviaba recién en el ciclo siguiente (diag confirmó:
            // "poll 20822ms · changes=0" seguido de "poll 454ms · changes=3").
            // Solución: promover + forzar SIEMPRE que haya cambios encolados en
            // _waitchanges durante un refresh en vuelo, sea cual sea `force`. Es el
            // mismo camino que force=true (que ya iba bien): la promoción deja
            // _syncchanges con el write, así el poll siguiente sale como WRITE
            // (COMMITED inmediato) y el server notifica al resto de clientes al
            // instante en vez de a los ~20s.
            if (force || this._waitchanges.length > 0){
                if (this._waitchanges.length > 0){
                    // Snapshot para restaurar si SortChanges falla: no dejar el
                    // changeset a medias (mismo criterio que el try/catch de arriba).
                    let _syncBackup = this._syncchanges.slice();
                    let _waitBackup = this._waitchanges.slice();

                    for(let _wait of this._waitchanges){
                        let _idx = this._IndexOfChange(_wait, this._syncchanges);
                        if (_idx != -1){
                            // Nunca descartar un do_insert existente salvo que _wait
                            // sea otro insert (el insert es la unica oportunidad de
                            // crear la fila; ver _cancelInflightRefresh). Insert +
                            // update del mismo objeto: se conservan AMBOS (insert
                            // primero, luego el update por SortChanges) para no dejar
                            // la fila sin crear -> @commit_error_relation_not_found.
                            let _existing = this._syncchanges[_idx];
                            let _existingIsInsert = _existing && _existing.entrynfo && (_existing.entrynfo['_actn'] == 'do_insert');
                            let _waitIsInsert = _wait && _wait.entrynfo && (_wait.entrynfo['_actn'] == 'do_insert');
                            if (!_existingIsInsert || _waitIsInsert){
                                this._syncchanges.splice(_idx, 1);   // dedup: gana la version mas nueva
                            }
                        }
                        this._syncchanges.push(_wait);
                    }

                    try {
                        this.SortChanges(this._syncchanges);
                        this._waitchanges = [];     // solo tras un sort exitoso

                        // Re-persistir el changeset definitivo: el SetChanges previo
                        // (arriba) guardo _waitchanges, pero el estado real ya es
                        // _syncchanges (rescatados por _cancelInflightRefresh +
                        // promovidos). Sin esto, un crash antes de terminar el
                        // _ForceRefresh recuperaria de storage un changeset incompleto.
                        await this.store.SetChanges(this._syncchanges, await this.wsstore.GetStorage());
                    }
                    catch(e){
                        // Restaurar ambos: el changeset promovido quedaria mal
                        // ordenado. Los cambios siguen intactos en _waitchanges y
                        // los envia el ciclo de fondo / _append_waitchanges (sin
                        // perdida, degradado al comportamiento previo).
                        this._syncchanges = _syncBackup;
                        this._waitchanges = _waitBackup;
                        // e puede no ser un Error con .message (string u otra cosa):
                        // extraer el mensaje de forma defensiva para no romper tr().
                        let _msg = (e && e.message) ? e.message : String(e);
                        this.toast.ShowAlert('danger', this.lang.tr(_msg));
                        // El sort es determinista: volveria a fallar en la promocion
                        // siguiente. Mismo tratamiento que el rechazo al encolar: los
                        // cambios NUEVOS de este lote salen de la cola, sus objetos se
                        // re-marcan (OnChangesRejected) y el llamante recibe false (no
                        // se imprime ni se cierra como guardado).
                        this.RollbackChanges(this._waitchanges, changes);
                        this.RollbackChanges(this._syncchanges, changes);
                        console.error("[COMMIT] changeset rejected while promoting waitchanges (" + changes.length + " entries)", e);
                        this._changesRejected.next(changes);
                        return false;
                    }
                }

                let _forced = performance.now();
                await this._ForceRefresh();
                console.info("[COMMIT] Forced commit completed in: " + (performance.now() - _forced).toFixed(2) + " ms");
            }
        }

        return true;
    }

    /**********************************/
    /* STARTS HERE                    */
    /**********************************/

    private _doGetDeviceUrl = 'device/getuuid.php';
    private _doSetDeviceUrl = 'device/setuuid.php';
    private _doGetTicketUrl = 'device/getticket.php';

    /*
        HOW DOES DEVICE ID WORKS?

        On startup a device-id is calculated. This value will remain until a browser update.
        On the GetDeviceId() function will try to get the stored value un localStorage
            If not found, we'll request the server for the last device-id stored for the current calculated device-id
        Well write the final device-id (stored in cache, stored in server or calculated one) in the localStorage
        Well update the server information with the current device-id and the calculated one

        This way, if the local cache is removed, then the device-id will be recovered from the server, based on the last update
        If the calculated value is updated at the same time the localStorage is cleared, the device-id will be lost!!
        In other circumstances, the device-id can be recovered.
    */

    private _device_fgprint = null;
    private _device_promise = null;
    get DeviceId(){
        if (this._session.device){
            return this._session.device;
        }

        if (!this._device_promise){
            this._device_promise = this.GetVisitorId();
        }

        return this._device_promise;
    }

    private async GetDeviceId(reset = false){
        console.info("[DEVICE ID]", this._device_fgprint);

        let _store = false, _server = false;   

        let _persistent = null;
        if (reset == false){
            _persistent = await this.store.GetDevice();
            if (_persistent){     // found in local storage
                _store = true;      
            }

            if (!_persistent){    // uuid not found in store: recover from server
                let _data = (await this.DoRequest(this._doGetDeviceUrl, { device: this._device_fgprint }));
                if (_data){
                    _server = (_data['errorcode'] == 0);   // found in server 
                    if (_server){
                        _persistent = _data['uuid'];
                    }
                    else {
                        if (_data['errorcode'] != 3){    // not found (this is not an error)
                            console.error("[DEVICE ID] GET returned errorcode [" + _data['errorcode'] + "]");
                        }
                    }
                }
            }
        }

        if (!_persistent){      // still not found: use calculated
            // Fallback al UUID nativo (this._session.device, fijado desde
            // this.device.uuid en GetVisitorId) si no hay fingerprint: en
            // entorno nativo el bloque de FingerprintJS no corre (la condicion
            // !_session.device es false) y _device_fgprint queda null. Con
            // reset=true se hace bypass de storage/servidor y aqui se caia a
            // null -> se persistia "null" y _session.device quedaba invalido.
            // El UUID nativo es el device id correcto en ese caso.
            _persistent = this._device_fgprint || this._session.device;
        }
        else {
            if (_store){
                console.info("[DEVICE ID] Found in local storage: '" + _persistent + "'")
            }
            else {
                console.info("[DEVICE ID] Found in remote storage: '" + _persistent + "'")
            }
        }

        // update the uuid and calc values in the server
        this.DoRequest(this._doSetDeviceUrl, { calc: this._session.device, uuid: _persistent }).then(
        _data => {
            if ((_data['errorcode'] != 0)){
                console.error("[DEVICE ID] SET (uuid) returned errorcode [" + _data['errorcode'] + "]");
            }
        });        

        this._session.device = _persistent;
        console.info("[STORAGE ID]", this._session.device);

        if (!_store) {  // update device id in storage
            this.store.SetDevice(_persistent).then(
            _data => {
                console.info("[DEVICE ID] Device ID stored!"); 
            });
        }
    }

    private async GetVisitorId(reset = false){
        let _browserid = "";    // https://stackoverflow.com/questions/27247806/generate-unique-id-for-each-device/27249628

        _browserid += window.navigator.mimeTypes.length.toString();
        _browserid += window.navigator.userAgent.replace(/\D+/g, '');
        _browserid += window.navigator.plugins.length;
        _browserid += window.screen.height.toString() || '';
        _browserid += window.screen.width.toString() || '';
        _browserid += window.screen.pixelDepth.toString() || '';

        _browserid = "BROWSER" + new CRC32(_browserid).value.toString(16);


        let _customid = " ";  
        let storedid = await this.store.GetDeviceCustom();
        if (storedid){
            _customid = " (" + storedid + ") ";
        }
        else {
            console.warn("[DEVICEID] No custom device string is provided!");
        }

        this._session.device = this.device.uuid;
        if (this._session.device){
            console.info("[DEVICEID] resolved from native: '" + this._session.device + "'");
        }

        // Fast path — when a persistent device ID is already cached
        // locally, GetDeviceId() below will prefer that one and throw
        // away whatever FingerprintJS computes here. Running the full
        // canvas/audio/WebGL fingerprint in that case costs up to
        // ~15-20 seconds on a datáfono / dev-mode build for nothing.
        // Only run FingerprintJS when there is really no stored ID and
        // when the caller hasn't asked for an explicit reset.
        let _storedDevice = null;
        if (!this._session.device && !reset){
            _storedDevice = await this.store.GetDevice();
            if (_storedDevice){
                this._device_fgprint = "BROWSER" + _customid + "[" + _browserid + "]";
                console.info("[DEVICEID] stored ID available — skipping FingerprintJS (fast path)");
            }
        }

        // `|| reset`: en un reset (SetVisitorId, p.ej. al fijar el Identificador
        // de terminal) hay que RECALCULAR el fingerprint aunque _device_fgprint ya
        // tenga el valor del arranque. Sin esto, el reset reutiliza el fingerprint
        // SIN customid calculado al cargar, el identificador nunca entra en el
        // device y por tanto tampoco en la serie fiscal (serial = CRC32(device +
        // place)). En nativo (device.uuid presente) la condicion sigue siendo
        // false: alli manda el uuid y esto no aplica.
        if (!this._session.device && (!this._device_fgprint || reset)){
            // Limpiar antes de recalcular: en un reset el valor previo es el del
            // arranque SIN customid. Si no se limpia y FingerprintJS lanza (o no
            // devuelve resultado), el fallback `if (!_device_fgprint)` no reasigna
            // y se reusaria el fingerprint viejo sin el customid -> la serie no
            // cambiaria en el camino de error. Con null, el fallback de abajo
            // reasigna con el customid actual.
            this._device_fgprint = null;

            let _promise = FingerprintJS.load();

            console.info("[DEVICEID] calculating fingerprint..");
            try {
                let _fingerprint = await _promise;
                if (_fingerprint){
                    let _result = await _fingerprint.get();
                    if (_result){
                        this._device_fgprint = "VISITOR" + _customid + "[" + _result.visitorId + " - " + _browserid + "]";
                    }
                }
            }
            catch (error) {
                console.error("[FINGERPRINT] Returned error (falling back to unsecure method)", error );
            }

            // Fallback fuera del catch: cubre TANTO la excepcion como el resultado
            // nulo. Usa el browserid con el customid actual para que el
            // identificador de terminal entre en el device tambien en fallo.
            if (!this._device_fgprint){
                this._device_fgprint = "BROWSER" + _customid + "[" + _browserid + "]";
            }
        }

        console.info("[DEVICEID] fingerprint is '" + this._device_fgprint + "'");
        await this.GetDeviceId(reset);
    }

    async SetVisitorId(){
        console.warn("[DEVICEID] Update is requested..")
        let _olddevice = this._session.device;
        await this.GetVisitorId(true);
        let _newdevice = this._session.device;

        if (_olddevice != _newdevice){
            return true;
        }

        return false;
    }

    // Fire-and-forget at the call site but the in-flight promise is
    // cached so NextInvoice() can await it before assigning a brand-new
    // invoice number — without that gate, a fresh install / cleared
    // localStorage would let the cashier create T00000001 while the
    // server already has T00000456 cached for this device series,
    // producing duplicate invoice numbers as soon as sync catches up.
    private _lastServedPromise: Promise<void> | null = null;
    public get LastServedReady(): Promise<void> {
        return this._lastServedPromise || Promise.resolve();
    }

    // Sincronico: empieza en true (no hay request pendiente) y se pone a false
    // durante el round-trip al server, true otra vez al terminar. Permite a la
    // UI gatear el "ready" sin awaitar la promise — sin esto, el cashier puede
    // crear el primer ticket antes de que LastServed resuelva y NextInvoice
    // bloquea el mutex 2-3s esperando al server.
    private _isLastServedReady = true;
    public get IsLastServedReady(): boolean {
        return this._isLastServedReady;
    }

    // Notifica cambios para que la UI vuelva a ejecutar change detection
    // sin tener que await la promise.
    private _lastServedTick = new Subject<void>();
    public OnLastServedReady = this._lastServedTick.asObservable();

    // Generation counter: cada GetLastServed obtiene un id incremental. El
    // finally solo libera _isLastServedReady si su gen sigue siendo el ultimo
    // — sin esto, llamadas solapadas (SetPlace -> SetPlace) liberaban el gate
    // al terminar la primera aunque la segunda siguiera en vuelo.
    private _lastServedGen = 0;

    private async GetLastServed(place){
        let _serial = this._session.serial;

        // check if data is available in the browser cache
        if (!place || !place.objid){
            return;
        }

        const _gen = ++this._lastServedGen;
        const _tini = performance.now();
        this._isLastServedReady = false;
        this._lastServedPromise = (async () => {
            try {
                let _data = (await this.DoRequest(this._doGetTicketUrl, { place: place.objid, serial: _serial }));

                // Instrumentacion: esta latencia gateaba el primer commit
                // tras el boot (NextInvoice esperaba el gate sin timeout —
                // medido en 36s con el pool PHP saturado por long-polls).
                // Si aparece sostenidamente >10s, revisar pm.max_children /
                // workers del server: getticket.php compite por worker con
                // los long-polls de 20s de todos los devices conectados.
                const _elapsed = (performance.now() - _tini) / 1000;
                if (_elapsed > 10){
                    console.warn("[DEVICE TICKET] getticket.php tardo " + _elapsed.toFixed(1) + "s — posible saturacion de workers PHP en el server");
                }
                else {
                    console.info("[DEVICE TICKET] getticket.php completado en " + _elapsed.toFixed(1) + "s");
                }
                if (_data){
                    let _success = (_data['errorcode'] == 0);
                    if (_success){
                        if (_data['last']){
                            this.store.SetLastTicket(place, _serial, _data['last']);
                        }
                    }
                    else {
                        console.error("[DEVICE TICKET] returned errorcode [" + _data['errorcode'] + "]");
                    }
                }
            }
            catch(error){
                console.error("[DEVICE TICKET] Server request failed.");
            }
            finally {
                // solo el ultimo GetLastServed en vuelo libera el gate
                if (_gen === this._lastServedGen){
                    this._isLastServedReady = true;
                    this._lastServedTick.next();
                }
            }
        })();
        await this._lastServedPromise;
    }

    // Tablas de cash-control aplazadas en el boot del datafono — se hidratan via ViewTill.
    public static readonly DATAFONO_LAZY_TABLES = new Set<string>([
        'AUDIT',
        'AUDITINFO',
        'AUDITTILL',
        'CASHCHANGE'
    ]);
    private _datafonoSkippedUpdates: any[] = null;
    private _datafonoTillHydrated: boolean = false;

    // Idempotente — llamar desde el handler de viewtill (spinner antes de abrir).
    HydrateTillTablesFromCache(): void {
        if (this._datafonoTillHydrated || !this._datafonoSkippedUpdates){
            this._datafonoTillHydrated = true;
            return;
        }
        const _updates = this._datafonoSkippedUpdates;
        this._datafonoSkippedUpdates = null;
        this._datafonoTillHydrated = true;
        if (this._pending && _updates && _updates.length > 0){
            console.info("[BOOT] Hydrating " + _updates.length + " deferred cash-control entries on-demand");
            this._pending.Concat(_updates, false);
        }
    }

    get IsTillHydrated(): boolean {
        return this._datafonoTillHydrated || !this.IsDatafono;
    }

    private _stageReady = new Subject<any>();
    public OnStageReady = this._stageReady.asObservable();

    get IsStageReady(){
        if (this.LoadTarget != 'SESSION'){
            return (this.LoadStage > 6);
        }
        else {  // check if new place
            if (this._session.place && !this._session.place.objid) {
                return this.LoadStage == 9;
            }
        }

        return false;
    }

    // Typed accessor so callers (and inner helpers like Pending) don't
    // have to reach into syncService['platform']._isDatafono and lose
    // visibility/refactor safety.
    get IsDatafono(): boolean {
        return !!this.platform?._isDatafono;
    }

    private _stage_subscription = null;
    private _recovered_subscription = null;
    private _online_subscription = null;
    private _stageready_notified = false;

    private _onDeviceIdReady = new Subject<any>();
    public OnDeviceIdReady = this._onDeviceIdReady.asObservable();

    constructor(private http: httpService, private lang: languageService, private alertCtrl: alertService, private toast: toastService, public devsvc: deviceService, private store: storeService, private device: Device, private platform: platformService){
        this.GetVisitorId().then(   // obtain a solid, unique device identifier
        async data => {
            this._onDeviceIdReady.next();
        });

        this._stage_subscription = this.OnRefreshStage.subscribe(
        stage => {
            console.info("[INITIALIZE] Loading stage [" + this.LoadTarget + " - " + this.LoadStage + "] items..");
            if (this.IsStageReady && !this._stageready_notified){
                this._stageready_notified = true;
                console.info("[INITIALIZE] Main load stage completed.");
                this._stageReady.next();
            }
        });

        // Pendientes recuperados de la copia WS durante un SetChanges (la
        // copia estaba ilegible y volvio a ser legible): re-encolarlos en
        // memoria. CommitChanges dedupe por _uuid/objid contra la lista
        // vigente, asi que los que ya esten en vuelo no se duplican.
        this._recovered_subscription = this.store.OnChangesRecovered.subscribe(
        (changes: Array<any>) => {
            console.warn("[STORAGE] " + changes.length + " pendientes recuperados de la copia WS — re-encolando");
            this.CommitChanges(changes as Array<SyncChange>, false);
        });

        // Pending-changes scan diferido tras Main load — antes bloqueaba el primer paint ~2-3s.
        const _pendingScanSub = this.OnStageReady.subscribe(() => {
            _pendingScanSub.unsubscribe();
            setTimeout(() => {
                // IIFE con try/catch — sin esto, un fallo en GetStorage
                // o CommitChanges escapa como unhandledrejection desde
                // el callback async del setTimeout.
                (async () => {
                    try {
                        console.info("[STORAGE] Searching for pending changes..");
                        let _changes = await this.store.GetChanges(await this.wsstore.GetStorage()) as Array <SyncChange>;
                        if (_changes && (_changes.length > 0)){
                            console.warn("[STORAGE] Found " + _changes.length + " changes from previous sessions");
                            await this.CommitChanges(_changes, true);
                        }
                        else {
                            console.info("[STORAGE] No changes from previous sessions (up to date)");
                        }
                    } catch(e){
                        console.error("[STORAGE] Pending-changes scan failed:", e);
                    }
                })();
            }, 0);
        });

        // Notificar al server que este device suelta la conexion. iOS Safari
        // NO garantiza 'unload' — cierra pestana, mata desde el switcher,
        // OOM kill... el evento se pierde y la conexion queda "activa" en el
        // server hasta el TTL, bloqueando reconexion desde el mismo device.
        //
        // ⚠ CRITICO: el endpoint unload.php llama incondicionalmente a
        // logout_session_and_notify → release_session (session.php) que
        // pone SESSION.status='DE'. NO hay dedup ni check de estado. Cada
        // beacon que enviamos ELIMINA la sesion en el server. Por eso:
        //
        //  - visibilitychange->hidden: NO disparamos beacon aqui. Un bloqueo
        //    de pantalla (habitual en iPhone en el bar) mataria la sesion y
        //    forzaria re-login al desbloquear. Deja la sesion viva y confia
        //    en que server-side el connection TTL la libere si el device
        //    realmente desaparece.
        //
        //  - pagehide con event.persisted=true: el navegador guarda la pagina
        //    en bfcache (Safari back/forward). La app volvera intacta y con
        //    la misma sesion — matarla aqui provoca re-login en cada
        //    back-forward. NO disparar.
        //
        //  - pagehide con event.persisted=false: cierre real (pestana cerrada,
        //    navegacion externa). ES la unica via segura para el beacon.
        //    iOS lo dispara fiablemente al cierre real de pestana o app kill.
        const _fireUnloadBeacon = () => {
            if (!this._session || !this._session.session){
                return;
            }
            const unloaddata = JSON.stringify({
                session: this._session.session,
                place: this._session.place? this._session.place.objid : null,
                table: this._session.qrcode? this._session.qrcode.objid : null
            });
            navigator.sendBeacon(AppConstants.baseURL + '/model/conn/unload.php', unloaddata);
        };

        this._pagehideHandler = event => {
            console.info("** PAGEHIDE (persisted=" + event.persisted + ") **");
            // Solo cierre real. persisted=true = bfcache, la sesion debe vivir.
            if (!event.persisted){
                _fireUnloadBeacon();
            }
        };
        window.addEventListener("pagehide", this._pagehideHandler);

        // Listen for page visibility changes to detect device lock/unlock
        // When device is locked, JavaScript timers pause, so periodic refresh doesn't occur
        // When page becomes visible again, force a refresh to validate session
        // Server will return errorcode == 2 if session expired
        this._visibilityChangeHandler = () => {
            if (document.hidden) {
                // Page is hidden (device locked or app in background)
                this._pageHiddenTimestamp = Date.now();
                console.info("[SESSION] Page hidden - pausing background activity");
                // NO beacon aqui — ver comentario extendido junto a la
                // declaracion de _fireUnloadBeacon. Un bloqueo de pantalla
                // NO debe matar la sesion server-side.
            } else if (this._pageHiddenTimestamp != null) {
                // Page is visible again (device unlocked or app in foreground)
                const elapsedSeconds = Math.floor((Date.now() - this._pageHiddenTimestamp) / 1000);
                console.info(`[SESSION] Page visible again after ${elapsedSeconds}s`);

                // Reenganchar tras volver a primer plano:
                //  - Lock largo (>60s): el XHR de sync seguro murio.
                //  - Lock corto (>=3s) CON refresh en vuelo: iOS pudo matar el
                //    XHR durante la suspension. Sin esto, el comandero (movil
                //    que bloquea/cambia de app en ventanas de segundos) quedaba
                //    con _onrefresh=true colgado hasta que el watchdog de
                //    _repeatRefresh lo cancela (~45s) — de ahi el "hay que
                //    recargar para que salgan las comandas". El reenganche es
                //    idempotente (generation++ + rescate de _sendchanges) y el
                //    dedup de 3s de _retryAfterWake evita el doble disparo con
                //    pageshow(bfcache). El umbral de 3s evita reenganchar en
                //    parpadeos triviales (<3s) donde el XHR sigue vivo.
                if (this._started && (elapsedSeconds > 60 || (this._onrefresh && elapsedSeconds >= 3))) {
                    this._retryAfterWake('visible ' + elapsedSeconds + 's' + (this._onrefresh ? ' refresh-en-vuelo' : ''));
                }

                this._pageHiddenTimestamp = null;
            }
        };

        // Add event listener
        document.addEventListener('visibilitychange', this._visibilityChangeHandler);

        // pageshow con event.persisted=true = restaurado desde bfcache (Safari).
        // visibilitychange NO se dispara SIEMPRE en ese caso, asi que sin este
        // handler los writes pendientes quedan huerfanos tras un
        // back-navigation en iOS. Cuando disparan AMBOS (pageshow y luego
        // visibilitychange), la ventana de dedup de _retryAfterWake evita el
        // doble refresh.
        this._pageshowHandler = (event) => {
            if (event.persisted && this._started) {
                this._retryAfterWake('bfcache');
            }
        };
        window.addEventListener('pageshow', this._pageshowHandler);

        // Cuando http.ts confirma que el server responde de nuevo, forzar
        // un refresh (y reenvio de pendientes). Sin esto, _repeatRefresh puede
        // estar en bucle con offlineObservable (status 0) mientras isOnline=false.
        this._online_subscription = this.http.OnOnline.subscribe(online => {
            if (online && this._started){
                this._offlineSyncWarned = false;
                this._retryAfterWake('network-online');
            }
        });
    }

    // Timestamp del ultimo wake-retry — dedup para el caso bfcache de iOS
    // donde pageshow(persisted=true) y visibilitychange(visible) disparan
    // seguidos: sin esta ventana, el segundo handler cancelaria (via
    // generation) el refresh que el primero acaba de lanzar y emitiria un
    // request duplicado.
    private _lastWakeRetryAt = 0;

    /** Recuperacion tras iOS sleep / bfcache restore. Secuencia:
     *  1. Cancelacion logica del XHR zombie via _refreshGeneration++ — sus
     *     handlers .then/.error/.catch haran no-op (no podemos abort()earlo:
     *     http.ts no expone la Subscription, MEDIUM#6 pendiente).
     *  2. RESCATE de los writes en vuelo: _prepare_syncronize los movio a
     *     _sendchanges. El guard de generation va a descartar el
     *     _onfailure_synchronize del zombie — que era el UNICO camino que
     *     los re-encolaba. Sin este rescate, el proximo _prepare_syncronize
     *     machacaria _sendchanges (comanda perdida sin toast) y el proximo
     *     _onsuccess_syncronize borraria incluso la copia persistida via
     *     store.ClearChanges. Re-enviar es seguro: el server es idempotente
     *     por _uuid (synchronize.php do_insert).
     *  3. PULL + PUSH de pendientes (_DoRefresh + _ForceRefresh si quedan).
     *  4. Reprogramar _repeatRefresh: si el zombie nunca dispara sus
     *     handlers, el .then que encadenaba el siguiente tick del loop no
     *     corre jamas y el refresh periodico moriria. _repeatRefresh hace
     *     clearTimeout del timer vigente, asi que llamarlo de mas es seguro. */
    private _retryAfterWake(reason: string){
        const _now = Date.now();
        if (_now - this._lastWakeRetryAt < 3000){
            console.info("[SESSION] wake retry skip (" + reason + ") — otro wake hace <3s");
            return;
        }
        this._lastWakeRetryAt = _now;

        // Marca de WAKE: SOLO los despertares (no commits, watchdog ni
        // refresh-error) son riesgo de zombie iOS. El guard de descarte en
        // _DoRefresh usa esto para NO tirar los reads de un poll superado por algo
        // que no sea un wake — restaura el instantáneo de 2.0.2.8. (No mover a
        // _cancelInflightRefresh: eso descartaría el poll del receptor superado por
        // un refresh-error justo al llegar el push -> regresión de ~20s.)
        this._wakeGeneration++;
        this._cancelInflightRefresh(reason);

        console.info("[SESSION] Validando + reintentando pendientes tras wake (" + reason + ")");
        this._DoRefresh(false, true).then(() => {
            // Reintento defensivo: writes que quedaron a medias durante el
            // sleep se reenvian ahora. Si no hay pendientes, _ForceRefresh
            // es no-op barato.
            const _pending = this._syncchanges.length + this._waitchanges.length;
            if (_pending > 0){
                console.info("[SESSION] Reintentando " + _pending + " writes pendientes tras despertar");
                return this._ForceRefresh();
            }
        }).catch(error => {
            console.error("[SESSION] Error validating session after " + reason + ":", error);
        }).then(() => {
            // Correr SIEMPRE (exito o error): reprogramar el loop periodico.
            if (this._rfrstatus == 'RUNNING'){
                this._repeatRefresh();
            }
        });
    }

    ngOnDestroy(){
        this._finalizeRefresh();

        if (this._stage_subscription){
            this._stage_subscription.unsubscribe();
            this._stage_subscription = null;
        }

        if (this._recovered_subscription){
            this._recovered_subscription.unsubscribe();
            this._recovered_subscription = null;
        }

        if (this._online_subscription){
            this._online_subscription.unsubscribe();
            this._online_subscription = null;
        }

        if (this._pending){
            this._pending.OnDestroy();
            this._pending = null;        
        }

        if (this._connected){
            this._connected.OnDestroy();
            this._connected = null;
        }

        // Remove visibility event listener
        if (this._visibilityChangeHandler){
            document.removeEventListener('visibilitychange', this._visibilityChangeHandler);
            this._visibilityChangeHandler = null;
        }

        // Remove pagehide/pageshow (bfcache + close). syncService es singleton
        // en la practica, pero hot-reload y tests provocan destroy sin recarga
        // real de window — sin unregister aqui, el listener viejo seguiria
        // vivo referenciando `this` muerto e iria disparando en cada
        // navegacion. Nit LOW de Bugbot revision 2026-07-04.
        if (this._pagehideHandler){
            window.removeEventListener('pagehide', this._pagehideHandler);
            this._pagehideHandler = null;
        }
        if (this._pageshowHandler){
            window.removeEventListener('pageshow', this._pageshowHandler);
            this._pageshowHandler = null;
        }
    }
    
    async Start(session){
        await this.DeviceId;    // wait until the device identifier  is ready

        if (session){
            this._session.session = session;
            await this.Connected.CreateConnection();

            this._initializeRefresh();
        }
        else {
            this._finalizeRefresh();
        }
    }

    async Stop(){
        console.info("[FINALIZE] Closing data service connection");
        await this.Connected.DeleteConnection();
        this._session.session = null;

        await this._finalizeRefresh();
        await this.store.ReleaseToken();
        console.info("[FINALIZE] Syncronization is stopped");
    }

    private _onReleased = new Subject<any>();
    public OnReleased = this._onReleased.asObservable();

    async Release(){
        console.info("[FINALIZE] Syncronization is released");
        this._session.Clear();
        this._onReleased.next();
    }

    async ResetStage(){
        await this._pauseRefresh();
        this._pending.Stage = 0;
    }

    async SetToken(session, token){
        this.Session.token = token;

        this.store.SetToken(token);
        let _token = await this.store.RecoverToken();
        if (_token){    // token available: update the session information
            _token.session = session
        }
        else {          // no token: initialize the token data
            _token = {
                session: session,
                placeid: null,
                orderid: null
            }
        }

        await this.store.UpdateToken(_token);
    }

    async SetUser(user){
        this.Session.user = user;
    }

    async SetPlace(place){
        await this._pauseRefresh();

        // notify only when place is available
        this._pending.DoNotify = (place != null);   

        // update the place in the session token
        if (this.Session.place != place){
            let _token = await this.store.RecoverToken();
            if (_token){
                _token.placeid = place ? place.objref : null;
            }
            await this.store.UpdateToken(_token);          
        }

        this._timestamp = 0;

        this.Session.place = place;
        this.Session.qrcode = null;
    
        if (place){
            // get the last served ticket numbers — fire-and-forget. This
            // hits device/getticket.php on the remote server and used to
            // add a ~9s wall of latency before the PLACE refresh could
            // even start because it was awaited. The only data it writes
            // back is the invoice-numbering counters used when *creating*
            // new tickets, so the PLACE sync does not need to wait for
            // it. Kicking it off in parallel drops the boot time by the
            // full round-trip of that call. NextInvoice() awaits
            // LastServedReady internally for the fresh-install case.
            this.GetLastServed(place);

            // simulate full load on new place
            if (!place.objid){
                this.Pending.Stage = 9;
            }
        }

        this._resumeRefresh();
    }

    async SetTable(qrcode){
        await this._pauseRefresh();

        this.Pending.Stage = 0;
        this._pending.DoNotify = (qrcode != null);   // notify only when table is available

        this._timestamp = 0;

        this.Session.place = null;
        this.Session.qrcode = qrcode;

        this._resumeRefresh();        
    }

    async SetTicket(ticket){
        let _token = await this.store.RecoverToken();
        if (_token){
            _token.orderid = ticket ? ticket.objref : null;
        }
        await this.store.UpdateToken(_token);          
    }

    private _localsync = false;
    async OnLocalService(){
        let _wsstore = null;

        if (this._localsync){
            return;     // already synchronized
        }

        let _till = this.Session.place ? this.Session.place.till : null;
        if (_till && _till.IsLocal){
            _wsstore = await this.wsstore.GetStorage();
            if (_wsstore){
                console.info("[STORAGE] Local service is connected!");
                this.Session.place.ExternalStoreReady = true;
            }
            else {
                console.warn("[STORAGE] Local service is required, but not connected!");
                this.Session.place.ExternalStoreReady = false;
            }
        }
        else {
            if (this.Session.place){
                this.Session.place.ExternalStoreReady = true;   // not required
            }
        }

        if (!_wsstore){
            return;     // service not available
        }

        this._localsync = true;

        // try/finally: si GetChanges o CommitChanges lanzan, _localsync se
        // quedaba en true para siempre y la recuperacion de pendientes no
        // volvia a intentarse hasta recargar la app — justo cuando mas se
        // necesita (WS reconectando tras corte).
        try {
            // recover any pending (unsent changes) from storage
            console.info("[STORAGE] Searching for pending changes..");
            let _changes = await this.store.GetChanges(await this.wsstore.GetStorage()) as Array <SyncChange>;
            if (_changes && (_changes.length > 0)){
                console.warn("[STORAGE] Found " + _changes.length + " changes from previous sessions");
                await this.CommitChanges(_changes, true);
            }
            else {
                console.info("[STORAGE] No changes from previous sessions (up to date)");
            }
        }
        catch(e){
            console.error("[STORAGE] Pending-changes recovery failed:", e);
        }
        finally {
            this._localsync = false;
        }
    }

    /**********************************/
    /* GENERIC SERVER REQUEST         */
    /**********************************/

    DoRequest(url, params = null, post = null, showtoast = true, headers = null, responseType = null, serializer = null, timeout = null){
        let _getparams = "?device=" + this._session.device + "&session=" + this._session.session + "&appvers=" + AppConstants.Version;

        if (!params || !('lang' in params)){   // language can be overriden in params
            _getparams += "&lang=" + this.lang.GetLanguage(true);
        }

        if (params){
            for(let _param in params){
                _getparams += "&" + _param + "=" + params[_param];
            }    
        }
        
        let _request = null;
        if (post){
            _request = this.http.post(AppConstants.baseURL + url + _getparams, post, (headers) ? headers : httpService.headers, responseType, serializer, timeout);
        }
        else {
            _request = this.http.get(AppConstants.baseURL + url + _getparams, true, (headers) ? headers : httpService.headers, responseType, timeout);
        }

        return new Promise(async (resolve, reject) => {
            let _subscription = _request.subscribe(
            data => {
                // Deteccion global de sesion expirada: los endpoints que solo dependen de
                // check_session (impresion, audit-bai, audit-vft, Stripe, MyPos, printer service,
                // imports, prvticket/newticket, login/password, login/resend, etc) que devuelvan
                // errorcode==2 disparan el dialogo. Antes solo el poll del sync engine lo detectaba,
                // dejando una ventana de 0-2s donde operaciones directas fallaban con "error
                // generico" sin explicar al usuario que su sesion estaba muerta.
                //
                // Los endpoints donde errorcode==2 puede significar "permission denied" en vez
                // de "sesion invalida" quedan excluidos por la denylist mas abajo — su cobertura
                // sigue siendo indirecta via el poll del sync engine (~500ms tick).
                //
                // No mutamos data ni bloqueamos el resolve: el caller sigue recibiendo su respuesta
                // y aplicando su propio manejo. _OnSessionExpired es idempotente (guard interno
                // _expired_notified + _rfrstatus=='EXPIRED') asi que llamadas repetidas son no-op.
                //
                // Guard _started (Session?.session != null): pre-sesion (login/stored.php,
                // login/activate.php, login/regenerate.php) devuelven Unauthorized por credenciales
                // invalidas / stored login corrupto, NO por sesion expirada. En ese punto no hay
                // sesion que expirar. Con este guard cubrimos ese flow sin denylist, permitiendo
                // que login/password.php y login/resend.php (que SI hacen check_session con sesion
                // activa) sigan cubiertos por el interceptor.
                //
                // Excepcion (dejar en denylist): login/login.php. En DEVRY el guest token invoca
                // sync.Start(session) antes del formulario de login (data.ts:1313), asi que cuando
                // el usuario introduce sus credenciales _started ya es true. Un password incorrecto
                // devolveria errorcode==2 y sin este skip explicito el interceptor abriria el modal
                // "sesion expirada" en lugar de dejar el flow de login mostrar su propio error.
                // Mismo caso si el usuario reintenta login con sesion ya activa (DoLogin desde
                // modal de re-login, cambio de user en TPV, etc).
                //
                // Denylist de endpoints donde errorcode==2 sigue siendo ambiguo tras el guard
                // _started (usuario ya logueado pero puede fallar por _authorize):
                //
                //   - protect/add.php, protect/get.php, protect/pemcert.php:
                //         _authorize() en protect/secure.php:149,215 devuelve Unauthorized si el
                //         user no tiene permisos sobre el recurso protegido.
                //
                //   - taxes/readcert.php, taxes/audit-sii.php, taxes/factura-e.php,
                //     taxes/readmail.php:
                //         llaman rdsecured() (protect/secure.php:207) que a su vez llama
                //         _authorize(). Si el user no tiene permisos sobre el certkey/token
                //         devuelven Unauthorized. Aunque estos endpoints tambien hacen
                //         check_session al inicio (donde Unauthorized SI seria sesion expirada),
                //         no podemos distinguir por URL las dos causas. Si la sesion realmente
                //         ha caducado, el poll del sync engine lo detectara en el proximo tick
                //         (~500ms) y disparara _OnSessionExpired de forma limpia.
                //
                // Alternativa mas robusta a medio plazo: allowlist de endpoints donde errorcode==2
                // solo puede ser check_session (mucho mas invasivo), o flag server-side explicito
                // session_expired=true en el response para desambiguar.
                if (this._started && data && typeof data === 'object' && data['errorcode'] == 2){
                    let _skipSessionExpired = url.indexOf('login/login.php') !== -1
                        || url.indexOf('protect/') !== -1
                        || url.indexOf('taxes/readcert.php') !== -1
                        || url.indexOf('taxes/audit-sii.php') !== -1
                        || url.indexOf('taxes/factura-e.php') !== -1
                        || url.indexOf('taxes/readmail.php') !== -1
                        // model/staffpin.php devuelve Unauthorized(2) por dos causas
                        // propias del endpoint (user sin acceso al local; rate limit
                        // del PIN agotado), no por sesión caducada. Sin este skip, el
                        // 11º intento de PIN dispararía el flujo "sesión expirada" en
                        // vez de rechazar el intento. Si la sesión SÍ caducó, el poll
                        // del sync engine lo detecta en el próximo tick (~500ms).
                        || url.indexOf('model/staffpin.php') !== -1
                        // model/pincheck.php (disponibilidad de PIN): igual que
                        // staffpin, devuelve Unauthorized(2) por causas propias
                        // (caller sin permiso de gestión; rate limit), no por sesión
                        // caducada. Sin el skip, consultar la disponibilidad dispararía
                        // el flujo "sesión expirada" (el cliente trata el 2 como tal).
                        || url.indexOf('model/pincheck.php') !== -1;
                    if (!_skipSessionExpired){
                        this._OnSessionExpired();
                    }
                }

                (data !== null) ? resolve(data) : reject("no response");
            },
            error => {
                reject(error);

                if (showtoast){
                    if (error.status != 0){
                        this.toast.ShowAlert('danger', this.lang.tr('@http_request_error', [ error.status ]));
                        console.error(this.lang.tr('@http_request_error', [ error.status ]));
                    }
                    else {
                        console.error("[HTTP] status = 0 (you are offline)");
                    }
                }
            },
            () => {
                _subscription.unsubscribe();
            });
        });
    }
}
